<script setup lang="ts">
import {
  addSoftwareDownloadCount,
  getSoftwareCommentInfo,
  getSoftwareComments,
  getSoftwareDetail,
  getSoftwareReleaseNotes,
  type CommentInfoData,
  type CommentTabOption,
  type SameTraderSoftware,
  type SoftwareLabel,
  type SoftwareComment,
  type SoftwareDetailParams,
} from '~/api/software-details'

import {
  getPopularSoftware,
  getSoftwareDownloadRanking,
  getSoftwareMonthlyActiveRanking,
  type SoftwareDeviceType,
  type SoftwarePlatformData,
  type SoftwareRankingData,
} from '~/api/software'
import { getTradingAppTraderAliases } from '~/api/store'
import { isChineseLocale } from '~/composables/useAppI18n'
import areaCodeCn from '~/assets/json/areaCodeCN.json'
import areaCodeEn from '~/assets/json/areaCodeEn.json'
import { resolveExternalUrl } from '~/utils/externalUrl'

type ReviewFilterKey = 'rating' | 'region' | 'time' | 'reply'
type CarouselSwipeTarget = 'screenshot' | 'review' | 'brand'
type RatingStarState = 'full' | 'half' | 'empty'
type SoftwareRankingType = 'active' | 'download' | 'popular'
type SoftwareRankingScope = 'global' | 'current'
type SoftwareRouteParams = Pick<
  SoftwareDetailParams,
  'marketId' | 'appBundleId' | 'softwareId' | 'traderCode' | 'deviceType'
>

interface ReviewFilterOption {
  label: string
  value: string
  flag?: string
}

interface ReviewDialogItem {
  id: string
  title: string
  rating: string
  region: string
  flag: string
  date: string
  author: string
  content: string
  replies: ReviewReplyItem[]
}

interface ReviewReplyItem {
  content: string
  date: string
}

interface BrandAppItem {
  icon: string
  name: string
  desc: string
  downloads: string
  online: boolean
  softwareId: string
  marketId: number
  appBundleId: string
}

interface SoftwareInfoItem {
  label: string
  value: string
  icon?: boolean
  href?: string
  action?: 'download'
}

interface CountryItem {
  countryCode?: string
  name?: string
}

interface SoftwareRankingConfig {
  type: SoftwareRankingType
  scope: SoftwareRankingScope
}

interface SoftwareLabelsSwipeState {
  pointerId: number
  startX: number
  lastX: number
  moved: boolean
}

type SoftwareRankingSources = Record<SoftwareRankingType, SoftwarePlatformData<SoftwareRankingData>>

definePageMeta({
  name: 'cryptoApps',
  path: '/crypto-apps/:id',
  hideDefaultTopNav: true,
})

// Hero 高度减去 Header 高度，滚动到该距离时背景完全不透明。
const SOFTWARE_HEADER_FADE_DISTANCE = 306
const WIKIBIT_MAIN_ORIGIN = 'https://www.wikibit.com'
const IOS_HTML_CLASS = 'IOS'
const ANDROID_HTML_CLASS = 'Android'
const SOFTWARE_ROUTE_QUERY_KEYS = new Set(['marketId', 'appBundleId', 'softwareId', 'traderCode', 'deviceType'])

const route = useRoute()
const { locale, localePath, t } = useAppI18n()
const { isRtl } = useRtl()
const appToast = useNuxtApp().$toast
const appStore = useAppStore()
const softwareRouteParams = resolveSoftwareRouteParams(route)
/** 详情页来源类型，控制面包屑第二级的展示与跳转。 */
const fromType = computed(() => Number(normalizeRouteValue(route.query.fromType)) || 0)
/** 详情地址中的设备类型用于维护 html 根节点 class，兼容旧页面的 IOS/Android 判断方式。 */
const softwareHtmlClass = computed(() =>
  Number(softwareRouteParams.deviceType) === 2 ? ANDROID_HTML_CLASS : IOS_HTML_CLASS,
)
const { data: softwarePageData } = await useAsyncData(
  `software-details-${softwareRouteParams.marketId}-${softwareRouteParams.appBundleId}-${softwareRouteParams.deviceType}`,
  async () => {
    const commonParams = {
      marketId: softwareRouteParams.marketId,
      appBundleId: softwareRouteParams.appBundleId,
    }
    const [detail, versions, commentInfo] = await Promise.all([
      getSoftwareDetail({
        ...commonParams,
        softwareId: softwareRouteParams.softwareId,
        traderCode: softwareRouteParams.traderCode,
        companyId: '',
        brandType: 1,
        deviceType: softwareRouteParams.deviceType,
        pageIndex: 1,
        pageSize: 4,
      }).catch(() => null),
      getSoftwareReleaseNotes({ ...commonParams, pageIndex: 1, pageSize: 4 }).catch(() => null),
      getSoftwareCommentInfo({ ...commonParams, countryCode: '999' }).catch(() => null),
    ])

    return { detail, versions, commentInfo }
  },
)

const activeScreenshotIndex = ref(0)
const screenshotTrackRef = ref<HTMLElement | null>(null)
const screenshotItemWidths = ref<number[]>([])
const selectedScreenshot = ref('')
const activeReviewIndex = ref(0)
const isDescriptionExpanded = ref(false)
const showDescriptionToggle = ref(false)
const descriptionContentRef = ref<HTMLElement | null>(null)
const descriptionCollapsedHeight = ref(288)
const softwareLabelsRef = ref<HTMLElement | null>(null)
const isVersionExpanded = ref(false)
const versionRecords = ref(softwarePageData.value?.versions?.items ?? [])
const versionTotal = ref(softwarePageData.value?.versions?.total ?? versionRecords.value.length)
const activePlatform = ref<'ios' | 'android'>(softwareRouteParams.deviceType === 2 ? 'android' : 'ios')
const initialSoftwareDeviceType = computed<SoftwareDeviceType>(() => (activePlatform.value === 'ios' ? 1 : 2))
const activeBrandAppIndex = ref(0)
const appIdRef = ref<HTMLElement | null>(null)
const downloadPopoverAnchorRef = ref<HTMLElement | null>(null)
const isDownloadPopoverVisible = ref(false)
const downloadPopoverPosition = ref({ top: 0, left: 0 })
const isHomeDownloadDialogVisible = ref(false)
const downloadDialogTitle = ref('')
const isSoftwareRankingDialogVisible = ref(false)
const isSoftwareRankingLoading = ref(false)
const softwareRankingType = ref<SoftwareRankingType>('active')
const softwareRankingScope = ref<SoftwareRankingScope>('global')
const softwareRankingData = ref<SoftwareRankingSources>({
  active: { ios: { global: [], current: [] }, android: { global: [], current: [] } },
  download: { ios: { global: [], current: [] }, android: { global: [], current: [] } },
  popular: { ios: { global: [], current: [] }, android: { global: [], current: [] } },
})
const isReviewDialogVisible = ref(false)
const reviewDialogTriggerRef = ref<HTMLButtonElement | null>(null)
const activeReviewFilterMenu = ref<ReviewFilterKey | null>(null)
const commentInfoData = ref<CommentInfoData | null>(softwarePageData.value?.commentInfo ?? null)
const reviewDialogInfoData = ref<CommentInfoData | null>(softwarePageData.value?.commentInfo ?? null)
const selectedReviewRating = ref(resolveFirstTabValue(commentInfoData.value?.commentTypes))
const selectedReviewRegion = ref(commentInfoData.value?.countryInfos?.[0]?.countryCode ?? '999')
const selectedReviewTimeRange = ref(resolvePreferredTabValue(commentInfoData.value?.timeRangeTypes, '近1年'))
const selectedReviewReply = ref(resolvePreferredTabValue(commentInfoData.value?.commentReplyTypes, '开发者回复'))
const reviewDialogItems = ref<ReviewDialogItem[]>([])
const isReviewDialogLoading = ref(false)
const isReviewDialogInitialLoading = ref(false)
const reviewDialogPageIndex = ref(0)
const reviewDialogTotal = ref(Number(softwarePageData.value?.commentInfo?.commentTotal ?? 0))
/** 收集评分文案节点，用于计算当前语言下所有标签的最大实际宽度。 */
const ratingLabelRefs = new Set<HTMLElement>()
const ratingLabelWidth = ref<number | null>(null)
const reviewDialogCardRefs = new Map<string, HTMLElement>()
let reviewDialogColumnHeights: [number, number] = [0, 0]
const expandedReviewContentIds = ref<string[]>([])
const overflowingReviewContentIds = ref<string[]>([])
const reviewContentNoEllipsisIds = ref<string[]>([])
const selectedPreviewReview = ref<ReviewDialogItem | null>(null)
const overflowingPreviewReviewIds = ref<string[]>([])
const previewReviewDetailContentRef = ref<HTMLElement | null>(null)
const isPreviewReviewDetailExpanded = ref(false)
const showPreviewReviewDetailToggle = ref(false)
const reviewContentRefs = new Map<string, HTMLElement>()
const previewReviewContentRefs = new Map<string, HTMLElement>()
let downloadPopoverHideTimer: ReturnType<typeof setTimeout> | undefined
let bodyOverflowBeforeScreenshotPreview = ''
let screenshotPreviewTrigger: HTMLElement | null = null
let bodyOverflowBeforeReviewDialog = ''
let bodyOverflowBeforePreviewReviewDialog = ''
let previewReviewDialogTrigger: HTMLElement | null = null
let carouselSwipeState: { pointerId: number; startX: number; target: CarouselSwipeTarget } | null = null
let lastCarouselSwipeAt = 0
let softwareLabelsSwipeState: SoftwareLabelsSwipeState | null = null
let softwareLabelsClickCandidate: SoftwareLabel | undefined
let suppressNextSoftwareLabelClick = false
let softwareRankingRequestSequence = 0
const softwareRankingRequests = new Map<SoftwareRankingType, Promise<SoftwarePlatformData<SoftwareRankingData>>>()

const CAROUSEL_SWIPE_THRESHOLD = 40
const FULL_STAR_ICON = 'https://resources1.interface003.com/web30/2026/09/09/e9400ad0-feb6-47b1-90b1-477931122d2b.png'
const EMPTY_STAR_ICON = 'https://resources1.interface003.com/web30/2026/09/09/c1ec4080-5895-4a49-b1c5-87fb1a07cfbf.png'

const SOFTWARE_LABEL_RANKING_CONFIG: Record<number, SoftwareRankingConfig> = {
  1: { type: 'popular', scope: 'global' },
  2: { type: 'popular', scope: 'current' },
  3: { type: 'active', scope: 'global' },
  4: { type: 'active', scope: 'current' },
  5: { type: 'download', scope: 'global' },
  6: { type: 'download', scope: 'current' },
}

const SOFTWARE_LABEL_HOME_ANCHORS: Record<number, string> = {
  7: '#tools',
  8: '#recommend',
}

const EMPTY_SOFTWARE_PLATFORM_RANKING_DATA = (): SoftwarePlatformData<SoftwareRankingData> => ({
  ios: { global: [], current: [] },
  android: { global: [], current: [] },
})

const EMPTY_SOFTWARE_RANKING_DATA = (): SoftwareRankingSources => ({
  active: EMPTY_SOFTWARE_PLATFORM_RANKING_DATA(),
  download: EMPTY_SOFTWARE_PLATFORM_RANKING_DATA(),
  popular: EMPTY_SOFTWARE_PLATFORM_RANKING_DATA(),
})

const SOFTWARE_RANKING_FETCHERS: Record<
  SoftwareRankingType,
  (deviceType: SoftwareDeviceType) => Promise<SoftwareRankingData>
> = {
  active: getSoftwareMonthlyActiveRanking,
  download: getSoftwareDownloadRanking,
  popular: getPopularSoftware,
}

const softwareDetail = computed(() => softwarePageData.value?.detail ?? null)
const { data: traderAliasMap } = await useAsyncData<Record<string, string>>(
  `software-detail-trader-alias-${softwareRouteParams.traderCode}`,
  () =>
    fromType.value === 4
      ? getTradingAppTraderAliases([softwareRouteParams.traderCode])
      : Promise.resolve<Record<string, string>>({}),
  {
    default: () => ({}),
  },
)
/** 优先读取运行时 html class，SSR 或未设置 class 时回退到详情 URL 的设备类型。 */
const isIosHtmlPlatform = computed(() => {
  if (import.meta.client) {
    if (document.documentElement.classList.contains(IOS_HTML_CLASS)) return true
    if (document.documentElement.classList.contains(ANDROID_HTML_CLASS)) return false
  }

  return softwareHtmlClass.value === IOS_HTML_CLASS
})
/** 交易所品牌别名由主站批量别名接口返回，避免使用展示名称拼接错误地址。 */
const softwareTraderAlias = computed(() => {
  const traderCode = softwareDetail.value?.traderCode || softwareRouteParams.traderCode

  return traderAliasMap.value?.[traderCode]?.trim() || ''
})
/** 根据 fromType 生成详情页面包屑第二级；来源参数缺失或 fromType=1 时隐藏该级。 */
const softwareBreadcrumbMiddle = computed(() => {
  if (!normalizeRouteValue(route.query.fromType) || fromType.value === 1) return null

  if (fromType.value === 2) {
    return {
      label: 'WikiBit',
      href: resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, '/', locale),
    }
  }

  if (fromType.value === 3) {
    const rankingSlug = isIosHtmlPlatform.value ? 'appdownload-ios' : 'appdownload-android'

    return {
      label: t('82477'),
      href: resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, `/best-exchanges/${rankingSlug}`, locale),
    }
  }

  if (fromType.value === 4) {
    return {
      label: softwareTraderAlias.value || softwareDetail.value?.traderName || '',
      href: softwareTraderAlias.value
        ? resolveExternalUrl(WIKIBIT_MAIN_ORIGIN, `/exchanges/${encodeURIComponent(softwareTraderAlias.value)}`, locale)
        : '',
    }
  }

  return {
    label: softwareDetail.value?.traderName || '',
    href: '',
  }
})
const countryGroups = computed(() => (isChineseLocale(locale) ? areaCodeCn : areaCodeEn))
const countries = computed(() => countryGroups.value.flatMap((group) => group.data as CountryItem[]))
const currentCountry = computed(() =>
  countries.value.find((country) => String(country.countryCode) === String(appStore.CountryCode)),
)
const currentCountryName = computed(
  () => currentCountry.value?.name || (isChineseLocale(locale) ? '英国' : 'United Kingdom'),
)
const platformItems = computed(() =>
  (['ios', 'android'] as const).filter((platform) => {
    const tabType = platform === 'ios' ? 1 : 2
    return Boolean(
      softwareDetail.value?.sameTraderSoftwares?.find((group) => Number(group.tabType) === tabType)?.items?.length,
    )
  }),
)
const softwareId = computed(() => softwareDetail.value?.softwareId || String(route.params.id ?? ''))
const screenshotItems = computed(() => softwareDetail.value?.screenshots ?? [])
const screenshotTrackWidth = computed(() =>
  screenshotItemWidths.value.reduce((width, itemWidth) => width + itemWidth, 0),
)
const maxScreenshotOffset = computed(() =>
  Math.max(0, screenshotTrackWidth.value + Math.max(0, screenshotItemWidths.value.length - 1) * 16 - 1200),
)
const screenshotOffsets = computed(() => {
  let offset = 0
  return screenshotItemWidths.value.map((width) => {
    const itemOffset = offset
    offset += width + 16
    return itemOffset
  })
})
const maxScreenshotIndex = computed(() => {
  const overflowIndex = screenshotOffsets.value.findIndex((offset) => offset >= maxScreenshotOffset.value)
  return overflowIndex >= 0 ? overflowIndex : Math.max(0, screenshotItems.value.length - 1)
})
const screenshotTrackOffset = computed(() =>
  Math.min(screenshotOffsets.value[activeScreenshotIndex.value] ?? 0, maxScreenshotOffset.value),
)
const screenshotTrackStyle = computed(() => ({
  transform: `translate3d(${(isRtl.value ? 1 : -1) * screenshotTrackOffset.value}px, 0, 0)`,
}))
const softwareIconUrl = computed(() => softwareDetail.value?.logo ?? '')
const softwareDownloadUrl = computed(() => softwareDetail.value?.downLoadUrl ?? '')
const softwareStatusLabel = computed(() => {
  if (softwareDetail.value?.status === 2) return t('89352')
  if (softwareDetail.value?.status === 1) return t('89470')
  return t('89353')
})
const softwareStatusClass = computed(() => (softwareDetail.value?.status === 2 ? 'text-[#22a06b]' : 'text-[#b7b9be]'))
const downloadPopoverStyle = computed(() => ({
  top: `${downloadPopoverPosition.value.top}px`,
  left: `${downloadPopoverPosition.value.left}px`,
}))
const softwareHeroStyle = computed(() => ({
  '--software-hero-image': softwareIconUrl.value ? `url("${softwareIconUrl.value}")` : 'none',
}))
const descriptionParagraphs = computed(() =>
  (softwareDetail.value?.description ?? '')
    .split(/\r?\n\s*\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean),
)
const visibleVersions = computed(() =>
  isVersionExpanded.value ? versionRecords.value : versionRecords.value.slice(0, 1),
)
const showVersionToggle = computed(() => Math.max(versionTotal.value, versionRecords.value.length) > 1)
const commentTotal = computed(() =>
  Number(commentInfoData.value?.commentTotal ?? softwareDetail.value?.commentTotal ?? 0),
)
const ratingValue = computed(() => Number(commentInfoData.value?.rating ?? softwareDetail.value?.rating ?? 0))
const ratingStars = computed<RatingStarState[]>(() =>
  Array.from({ length: 5 }, (_, index) => {
    const position = index + 1
    if (ratingValue.value >= position) return 'full'
    if (ratingValue.value > position - 1) return 'half'
    return 'empty'
  }),
)
const ratingItems = computed(() => {
  const source = commentInfoData.value ?? softwareDetail.value
  const total = commentTotal.value
  const items = [
    { label: t('89357'), value: Number(source?.commentPositive ?? 0) },
    { label: t('89358'), value: Number(source?.commentNeutral ?? 0) },
    { label: t('89359'), value: Number(source?.commentGood ?? 0) },
    { label: t('89360'), value: Number(source?.commentNegative ?? 0) },
    { label: t('89361'), value: Number(source?.commentPoor ?? 0) },
  ]

  return items.map((item) => ({
    ...item,
    width: `${total > 0 ? Math.min(Math.floor((item.value / total) * 100), 100) : 0}%`,
  }))
})
/** 按展示列拆分评分项，让同列标签共享当前文案的最大宽度。 */
const ratingColumns = computed(() => [
  ratingItems.value.filter((_item, index) => index % 3 === 0),
  ratingItems.value.filter((_item, index) => index % 3 === 1),
  ratingItems.value.filter((_item, index) => index % 3 === 2),
])
const reviewDialogRatingItems = computed(() => {
  const source = reviewDialogInfoData.value ?? softwareDetail.value
  const total = Number(reviewDialogInfoData.value?.commentTotal ?? 0)
  const items = [
    { label: t('89357'), value: Number(source?.commentPositive ?? 0) },
    { label: t('89358'), value: Number(source?.commentNeutral ?? 0) },
    { label: t('89359'), value: Number(source?.commentGood ?? 0) },
    { label: t('89360'), value: Number(source?.commentNegative ?? 0) },
    { label: t('89361'), value: Number(source?.commentPoor ?? 0) },
  ]

  return items.map((item) => ({
    ...item,
    width: `${total > 0 ? Math.min(Math.floor((item.value / total) * 100), 100) : 0}%`,
  }))
})
/** 按弹窗的两列布局拆分评分项，让每列的标签宽度独立自适应。 */
const reviewDialogRatingColumns = computed(() => [
  reviewDialogRatingItems.value.filter((_item, index) => index % 2 === 0),
  reviewDialogRatingItems.value.filter((_item, index) => index % 2 === 1),
])
const ratingLabelGridStyle = computed(() => ({
  gridTemplateColumns: `${ratingLabelWidth.value ? `${ratingLabelWidth.value}px` : 'max-content'} minmax(0, 1fr)`,
}))
const reviewItems = computed(() => (softwareDetail.value?.commentInfos ?? []).map(normalizeReviewItem))
const reviewRatingOptions = computed(() => normalizeTabOptions(reviewDialogInfoData.value?.commentTypes))
const reviewRegionOptions = computed<ReviewFilterOption[]>(() =>
  (reviewDialogInfoData.value?.countryInfos ?? []).map((item) => ({
    label: item.countryName,
    value: item.countryCode,
    flag: item.flag,
  })),
)
const reviewTimeOptions = computed(() => normalizeTabOptions(reviewDialogInfoData.value?.timeRangeTypes))
const reviewReplyOptions = computed(() => normalizeTabOptions(reviewDialogInfoData.value?.commentReplyTypes))
const reviewDialogFilters = computed(() => [
  {
    key: 'rating' as const,
    label: resolveFilterLabel(reviewRatingOptions.value, selectedReviewRating.value, t('89374')),
    options: reviewRatingOptions.value,
  },
  {
    key: 'region' as const,
    label: resolveFilterLabel(reviewRegionOptions.value, selectedReviewRegion.value, t('89375')),
    options: reviewRegionOptions.value,
  },
  {
    key: 'time' as const,
    label: resolveFilterLabel(reviewTimeOptions.value, selectedReviewTimeRange.value, t('89376')),
    options: reviewTimeOptions.value,
  },
  {
    key: 'reply' as const,
    label: resolveFilterLabel(reviewReplyOptions.value, selectedReviewReply.value, t('89373')),
    options: reviewReplyOptions.value,
  },
])
const filteredReviewDialogItems = computed(() => reviewDialogItems.value)
const reviewDialogColumns = ref<[ReviewDialogItem[], ReviewDialogItem[]]>([[], []])
const visibleReviewColumnCount = computed(() => Math.max(1, Math.min(reviewItems.value.length, 3)))
const reviewItemWidth = computed(
  () => (1200 - (visibleReviewColumnCount.value - 1) * 20) / visibleReviewColumnCount.value,
)
const maxReviewIndex = computed(() => Math.max(0, reviewItems.value.length - 3))
const reviewItemStyle = computed(() => ({
  width: `${reviewItemWidth.value}px`,
}))
const reviewTrackStyle = computed(() => ({
  transform: `translate3d(${(isRtl.value ? 1 : -1) * activeReviewIndex.value * (reviewItemWidth.value + 20)}px, 0, 0)`,
}))
const softwareInfoGroups = computed<SoftwareInfoItem[][]>(() => [
  [
    {
      label: t('89363'),
      value: softwareDetail.value?.developer?.name || '--',
      icon: true,
      action: softwareDetail.value?.developer?.name ? 'download' : undefined,
    },
    { label: 'APP ID', value: softwareDetail.value?.appBundleId || '--' },
    { label: t('89348'), value: softwareDetail.value?.shortIssueRegion || softwareDetail.value?.issueRegion || '--' },
  ],
  [
    { label: t('84678'), value: formatApiDate(softwareDetail.value?.releaseTime) },
    { label: t('89365'), value: softwareDetail.value?.version || '--' },
    { label: t('89366'), value: softwareDetail.value?.shortLanguages || softwareDetail.value?.languages || '--' },
  ],
  [
    { label: t('89364'), value: formatApiDate(softwareDetail.value?.lastReleaseTime) },
    {
      label: t('89355'),
      value: softwareDetail.value?.shortCompatibility || softwareDetail.value?.compatibility || '--',
    },
    {
      label: t('89367'),
      value: softwareDetail.value?.supportWeb || softwareDetail.value?.developer?.website || '--',
      href: normalizeExternalUrl(softwareDetail.value?.supportWeb || softwareDetail.value?.developer?.website),
    },
  ],
])
const visibleBrandApps = computed<BrandAppItem[]>(() => {
  const tabType = activePlatform.value === 'ios' ? 1 : 2
  const group = softwareDetail.value?.sameTraderSoftwares?.find((item) => Number(item.tabType) === tabType)
  return (group?.items ?? []).map(normalizeBrandApp)
})
const hasBrandApps = computed(() => platformItems.value.length > 0)
/** 同品牌软件按三列两行排列，先填满第一行后再排列第二行。 */
const brandAppColumns = computed(() => {
  const apps = visibleBrandApps.value
  const appsPerPage = 6

  return Array.from({ length: Math.ceil(apps.length / appsPerPage) }, (_page, pageIndex) => {
    const pageApps = apps.slice(pageIndex * appsPerPage, (pageIndex + 1) * appsPerPage)
    const columnCount = Math.min(3, pageApps.length)

    return Array.from({ length: columnCount }, (_column, columnIndex) =>
      [pageApps[columnIndex], pageApps[columnIndex + 3]].filter((app): app is BrandAppItem => Boolean(app)),
    )
  }).flat()
})
/** 同品牌软件不足四个时只展示一排卡片，超过三项时保留两排高度。 */
const brandAppCarouselHeightClass = computed(() =>
  visibleBrandApps.value.length > 3 ? 'h-[220px]' : 'h-[100px]',
)
const maxBrandAppIndex = computed(() => Math.max(0, brandAppColumns.value.length - 3))
const brandAppTrackStyle = computed(() => ({
  transform: `translate3d(${(isRtl.value ? 1 : -1) * activeBrandAppIndex.value * 406.6667}px, 0, 0)`,
}))
const platformIconSrc = computed(() =>
  activePlatform.value === 'ios'
    ? 'https://resources1.interface003.com/web30/2026/09/08/314508e6-2437-4606-aa9e-c0fabccd61c4.png'
    : 'https://resources1.interface003.com/web30/2026/09/08/13f0f428-9330-4083-a3ca-bc5f4704eb4b.png',
)
watch(activePlatform, () => {
  activeBrandAppIndex.value = 0
})

watch(
  platformItems,
  (availablePlatforms) => {
    if (!availablePlatforms.includes(activePlatform.value)) {
      activePlatform.value = availablePlatforms[0] ?? activePlatform.value
    }
  },
  { immediate: true },
)

watch(screenshotItems, () => {
  activeScreenshotIndex.value = 0
  void nextTick(updateScreenshotWidths)
})

watch(descriptionParagraphs, () => {
  isDescriptionExpanded.value = false
  void nextTick(updateDescriptionOverflow)
})

watch(reviewItems, () => {
  activeReviewIndex.value = 0
  previewReviewContentRefs.clear()
  void nextTick(updatePreviewReviewOverflow)
})

watch(reviewDialogItems, () => {
  reviewContentRefs.clear()
  overflowingReviewContentIds.value = []
  reviewContentNoEllipsisIds.value = []
  expandedReviewContentIds.value = []
  void nextTick(updateReviewContentOverflow)
})

watch([ratingItems, reviewDialogRatingItems, locale], () => {
  void nextTick(updateRatingLabelWidth)
})

/** 确保详情页标题带有 WikiStore 品牌标识。 */
const softwareName = computed(() => softwareDetail.value?.name || '')
const resolveSeoText = (key: string) => t(key, [softwareName.value]).replaceAll('{0}', softwareName.value)

const cryptoAppsTitle = (await getSeoLocaleMessage(locale, '89584')).replaceAll('{0}', softwareName.value)
useHead(() => ({
  title: cryptoAppsTitle,
  htmlAttrs: {
    class: softwareHtmlClass.value,
  },
  meta: [
    {
      name: 'description',
      content: resolveSeoText('89585'),
    },
  ],
}))

onMounted(() => {
  void nextTick(updateDescriptionOverflow)
  void nextTick(updateScreenshotWidths)
  void nextTick(updatePreviewReviewOverflow)
  void nextTick(updateRatingLabelWidth)
  window.addEventListener('scroll', hideDownloadPopover, true)
  window.addEventListener('resize', updateDownloadPopoverPosition)
  window.addEventListener('resize', updateDescriptionOverflow)
  window.addEventListener('resize', updateScreenshotWidths)
  window.addEventListener('resize', updateRatingLabelWidth)
  window.addEventListener('resize', updateReviewContentOverflow)
  window.addEventListener('resize', updatePreviewReviewOverflow)
  window.addEventListener('resize', updatePreviewReviewDetailOverflow)
  window.addEventListener('keydown', handleDialogKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', hideDownloadPopover, true)
  window.removeEventListener('resize', updateDownloadPopoverPosition)
  window.removeEventListener('resize', updateDescriptionOverflow)
  window.removeEventListener('resize', updateScreenshotWidths)
  window.removeEventListener('resize', updateRatingLabelWidth)
  window.removeEventListener('resize', updateReviewContentOverflow)
  window.removeEventListener('resize', updatePreviewReviewOverflow)
  window.removeEventListener('resize', updatePreviewReviewDetailOverflow)
  window.removeEventListener('keydown', handleDialogKeydown)
  restoreScreenshotPreviewBodyScroll()
  restoreReviewDialogBodyScroll()
  restorePreviewReviewDialogBodyScroll()
  if (downloadPopoverHideTimer) clearTimeout(downloadPopoverHideTimer)
  ratingLabelRefs.clear()
})

/** 注册评分文案节点，供统一标签列宽计算使用。 */
function setRatingLabelRef(element: HTMLElement | null): void {
  if (element) ratingLabelRefs.add(element)
}

/** 根据当前渲染文案的最大宽度，统一评分条的标签列宽。 */
function updateRatingLabelWidth(): void {
  for (const element of ratingLabelRefs) {
    if (!element.isConnected) ratingLabelRefs.delete(element)
  }

  const maxWidth = Math.max(0, ...Array.from(ratingLabelRefs, (element) => element.getBoundingClientRect().width))
  ratingLabelWidth.value = maxWidth > 0 ? Math.ceil(maxWidth) : null
}

/** 将路由参数统一为字符串，兼容 Nuxt 查询参数数组。 */
function normalizeRouteValue(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] ?? '')
  return String(value ?? '')
}

/** 解析 crypto-apps 路径中的 marketId、appBundleId、traderCode 和设备类型。 */
function resolveSoftwareRouteParams(currentRoute: ReturnType<typeof useRoute>): SoftwareRouteParams {
  const routeId = normalizeRouteValue(currentRoute.params.id).replace(/\.html$/, '')
  const queryTraderCode = normalizeRouteValue(currentRoute.query.traderCode)

  if (queryTraderCode) {
    return {
      marketId: normalizeRouteValue(currentRoute.query.marketId),
      appBundleId: normalizeRouteValue(currentRoute.query.appBundleId),
      softwareId: routeId,
      traderCode: queryTraderCode,
      deviceType: Number(normalizeRouteValue(currentRoute.query.deviceType)) || '',
    }
  }

  const routeParts = routeId.split('-')
  const marketId = routeParts.shift() ?? ''
  const deviceType = Number(routeParts.pop()) || ''
  const traderCode = routeParts.pop() ?? ''

  return {
    marketId,
    appBundleId: routeParts.join('-'),
    softwareId: '',
    traderCode,
    deviceType,
  }
}

/** 将接口筛选项转换为页面下拉菜单统一结构。 */
function normalizeTabOptions(options?: CommentTabOption[]): ReviewFilterOption[] {
  return (options ?? []).map((item) => ({ label: item.tabTypeText, value: String(item.tabType) }))
}

/** 默认使用接口首个筛选值，接口无数据时保持旧接口的 0 值约定。 */
function resolveFirstTabValue(options?: CommentTabOption[]): string {
  return String(options?.[0]?.tabType ?? 0)
}

/** 优先选择设计稿指定筛选项，接口未返回时回退到首项。 */
function resolvePreferredTabValue(options: CommentTabOption[] | undefined, keyword: string): string {
  const preferred = options?.find((item) => item.tabTypeText.includes(keyword))
  return String(preferred?.tabType ?? options?.[0]?.tabType ?? 0)
}

/** 获取当前筛选值对应文案。 */
function resolveFilterLabel(options: ReviewFilterOption[], value: string, fallback: string): string {
  return options.find((option) => option.value === value)?.label ?? fallback
}

/** 接口时间同时兼容秒级时间戳、毫秒时间戳及标准日期字符串。 */
function parseApiDate(value?: string | number): Date | null {
  if (value === undefined || value === null || value === '') return null
  const numericValue = Number(value)
  const date = Number.isFinite(numericValue)
    ? new Date(String(Math.trunc(numericValue)).length <= 10 ? numericValue * 1000 : numericValue)
    : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** 软件信息日期使用稳定的 YYYY-MM-DD 格式。 */
function formatApiDate(value?: string | number): string {
  const date = parseApiDate(value)
  if (!date) return '--'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** 版本时间轴沿用设计稿的中文年月日格式。 */
function formatVersionDate(value?: string | number): string {
  return formatApiDate(value).replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$1年$2月$3日')
}

/** 评论日期统一为完整日期，卡片中再截取月日。 */
function formatCommentDate(value?: string | number): string {
  return formatApiDate(value).replace('--', '')
}

/** 详情接口与评论列表接口共用同一评论展示模型。 */
function normalizeReviewItem(comment: SoftwareComment, index: number): ReviewDialogItem {
  const country = reviewDialogInfoData.value?.countryInfos?.find((item) => item.countryCode === comment.countryCode)
  const replies = (comment.replies ?? []).map((reply) => ({
    content: reply.replyContent ?? '',
    date: formatCommentDate(reply.replyTime),
  }))

  return {
    id: String(comment.commentId ?? comment.id ?? `${comment.commentTime ?? 'comment'}-${index}`),
    title: comment.title ?? '',
    rating: comment.starRatingText || (comment.starRating ? `${comment.starRating}星` : ''),
    region: comment.countryName || comment.region || country?.countryName || '',
    flag: comment.flag || country?.flag || '',
    date: formatCommentDate(comment.commentTime),
    author: comment.userName ?? '',
    content: comment.content ?? '',
    replies,
  }
}

/** 同品牌软件保持接口数组顺序，只转换页面需要的字段。 */
function normalizeBrandApp(app: SameTraderSoftware): BrandAppItem {
  return {
    icon: app.logo,
    name: app.name,
    desc: app.title,
    downloads: formatNumber(app.downloadNum),
    online: !app.isDown,
    softwareId: app.softwareId,
    marketId: app.marketId,
    appBundleId: app.appBundleId,
  }
}

/** 拼接同品牌 crypto-apps 详情页地址。 */
function getBrandAppPath(app: BrandAppItem): string {
  const deviceType = app.marketId === 11 ? 2 : 1
  const traderCode = softwareDetail.value?.traderCode || softwareRouteParams.traderCode
  const routeId = `${app.marketId}-${app.appBundleId}-${traderCode}-${deviceType}`
  return localePath(`/crypto-apps/${routeId}`)
}

/** 同品牌软件跳转时继承来源和追踪参数，避免旧版查询式路由参数覆盖目标软件路径。 */
function getBrandAppQuery() {
  return Object.fromEntries(Object.entries(route.query).filter(([key]) => !SOFTWARE_ROUTE_QUERY_KEYS.has(key)))
}

/** 下载量和评分数量统一使用本地化千分位。 */
function formatNumber(value?: string | number): string {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? new Intl.NumberFormat().format(numberValue) : '--'
}

/** 接口标题包含少量 HTML 实体，插值前做安全的文本解码。 */
function decodeHtmlText(value?: string): string {
  return (value ?? '')
    .replaceAll('&amp;', '&')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
}

/** 官方网址缺少协议时补全为 HTTPS，确保外链能够正常打开。 */
function normalizeExternalUrl(value?: string): string {
  const url = value?.trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('//')) return `https:${url}`
  return `https://${url}`
}

/** 描述超过 12 行时显示展开入口，并避免折叠位置落在段落间距内。 */
function updateDescriptionOverflow() {
  const container = descriptionContentRef.value
  if (!container) return

  const paragraphs = Array.from(container.children) as HTMLElement[]
  const containerStyle = window.getComputedStyle(container)
  const lineHeight = Number.parseFloat(containerStyle.lineHeight) || 24
  const paragraphGap = Number.parseFloat(containerStyle.rowGap) || 0
  const maxTextHeight = lineHeight * 12
  const textHeight = paragraphs.reduce((height, paragraph) => height + paragraph.scrollHeight, 0)
  let remainingTextHeight = maxTextHeight
  let collapsedHeight = 0

  for (const [index, paragraph] of paragraphs.entries()) {
    if (remainingTextHeight <= 0) break
    if (index > 0) collapsedHeight += paragraphGap

    const visibleTextHeight = Math.min(paragraph.scrollHeight, remainingTextHeight)
    collapsedHeight += visibleTextHeight
    remainingTextHeight -= visibleTextHeight
  }

  descriptionCollapsedHeight.value = Math.ceil(collapsedHeight)
  showDescriptionToggle.value = textHeight > maxTextHeight
}

/** 记录截图按原始比例渲染后的实际宽度，供轮播逐张计算位移。 */
function updateScreenshotWidths() {
  screenshotItemWidths.value = Array.from(screenshotTrackRef.value?.children ?? []).map(
    (item) => (item as HTMLElement).getBoundingClientRect().width,
  )
  activeScreenshotIndex.value = Math.min(activeScreenshotIndex.value, maxScreenshotIndex.value)
}

// 每次仅移动一张，并将索引限制在首尾范围内。
const moveScreenshot = (direction: -1 | 1) => {
  activeScreenshotIndex.value = Math.min(Math.max(activeScreenshotIndex.value + direction, 0), maxScreenshotIndex.value)
}

/** 打开当前截图的大图预览，轮播刚完成拖拽时跳过随后的 click。 */
const openScreenshotPreview = (screenshot: string, event: MouseEvent | KeyboardEvent) => {
  if (!screenshot || Date.now() - lastCarouselSwipeAt < 250) return

  screenshotPreviewTrigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null
  bodyOverflowBeforeScreenshotPreview = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  selectedScreenshot.value = screenshot
}

/** 组件卸载或路由离开时恢复打开预览前的页面滚动状态。 */
const restoreScreenshotPreviewBodyScroll = () => {
  if (!import.meta.client || !selectedScreenshot.value) return
  document.body.style.overflow = bodyOverflowBeforeScreenshotPreview
}

/** 关闭截图预览并把焦点交还给触发截图，保证键盘操作可继续。 */
const closeScreenshotPreview = () => {
  if (!selectedScreenshot.value) return
  document.body.style.overflow = bodyOverflowBeforeScreenshotPreview
  selectedScreenshot.value = ''
  void nextTick(() => screenshotPreviewTrigger?.focus())
}

/** 评分评论每次移动一列，并限制在可展示的首尾范围内。 */
const moveReviews = (direction: -1 | 1) => {
  activeReviewIndex.value = Math.min(Math.max(activeReviewIndex.value + direction, 0), maxReviewIndex.value)
}

const restoreReviewDialogBodyScroll = () => {
  if (!import.meta.client || !isReviewDialogVisible.value) return
  document.body.style.overflow = bodyOverflowBeforeReviewDialog
}

const openReviewDialog = async () => {
  if (isReviewDialogVisible.value) return
  bodyOverflowBeforeReviewDialog = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  activeReviewFilterMenu.value = null
  expandedReviewContentIds.value = []
  isReviewDialogVisible.value = true
  reviewDialogItems.value = []
  reviewDialogColumns.value = [[], []]
  reviewDialogColumnHeights = [0, 0]
  reviewDialogCardRefs.clear()
  isReviewDialogInitialLoading.value = true
  await loadReviewDialogItems(true)
  isReviewDialogInitialLoading.value = false
}

const closeReviewDialog = () => {
  if (!isReviewDialogVisible.value) return
  document.body.style.overflow = bodyOverflowBeforeReviewDialog
  activeReviewFilterMenu.value = null
  isReviewDialogVisible.value = false
  void nextTick(() => reviewDialogTriggerRef.value?.focus())
}

function handleDialogKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (selectedScreenshot.value) {
    closeScreenshotPreview()
    return
  }
  if (selectedPreviewReview.value) {
    closePreviewReviewDialog()
    return
  }
  if (activeReviewFilterMenu.value) {
    activeReviewFilterMenu.value = null
    return
  }
  closeReviewDialog()
}

/** 同一时间只展示一个评论筛选菜单。 */
const toggleReviewFilterMenu = (key: ReviewFilterKey) => {
  activeReviewFilterMenu.value = activeReviewFilterMenu.value === key ? null : key
}

/** 更新指定评论筛选项，并关闭当前下拉菜单。 */
const selectReviewFilter = async (key: ReviewFilterKey, value: string) => {
  if (key === 'rating') selectedReviewRating.value = value
  if (key === 'time') selectedReviewTimeRange.value = value
  if (key === 'reply') selectedReviewReply.value = value
  if (key === 'region') {
    selectedReviewRegion.value = value
    const nextCommentInfo = await getSoftwareCommentInfo({
      marketId: softwareRouteParams.marketId,
      appBundleId: softwareRouteParams.appBundleId,
      countryCode: value,
    }).catch(() => null)
    if (nextCommentInfo) {
      reviewDialogInfoData.value = nextCommentInfo
      selectedReviewRating.value = resolveFirstTabValue(nextCommentInfo.commentTypes)
      selectedReviewTimeRange.value = resolvePreferredTabValue(nextCommentInfo.timeRangeTypes, '近1年')
      selectedReviewReply.value = resolvePreferredTabValue(nextCommentInfo.commentReplyTypes, '开发者回复')
    }
  }
  activeReviewFilterMenu.value = null
  await loadReviewDialogItems(true)
}

/** 评论弹窗切换筛选时保留当前列表，等接口返回后再整体替换，避免闪烁。 */
const loadReviewDialogItems = async (reset = false) => {
  if (
    isReviewDialogLoading.value ||
    (!reset && reviewDialogPageIndex.value > 0 && reviewDialogItems.value.length >= reviewDialogTotal.value)
  ) {
    return
  }

  isReviewDialogLoading.value = true
  const pageSize = 10
  const pageIndex = reset ? 1 : reviewDialogPageIndex.value + 1
  const result = await getSoftwareComments({
    marketId: softwareRouteParams.marketId,
    appBundleId: softwareRouteParams.appBundleId,
    commentType: selectedReviewRating.value,
    countryCode: selectedReviewRegion.value,
    timeRangeType: selectedReviewTimeRange.value,
    commentReplyType: selectedReviewReply.value,
    pageIndex,
    pageSize,
  }).catch(() => null)
  if (!result) {
    isReviewDialogLoading.value = false
    return
  }

  const items = result.items ?? []
  const baseIndex = reset ? 0 : reviewDialogItems.value.length
  const nextItems = items.map((item, index) => normalizeReviewItem(item, baseIndex + index))
  const existingIds = reset ? new Set<string>() : new Set(reviewDialogItems.value.map((item) => item.id))
  const appendedIds = new Set<string>()
  const uniqueNextItems = nextItems.filter((item) => {
    if (existingIds.has(item.id) || appendedIds.has(item.id)) return false
    appendedIds.add(item.id)
    return true
  })

  reviewDialogPageIndex.value = pageIndex
  reviewDialogTotal.value = Number(result.total ?? 0)
  if (reset) {
    reviewDialogItems.value = uniqueNextItems
    reviewDialogColumns.value = [[], []]
    reviewDialogColumnHeights = [0, 0]
    reviewDialogCardRefs.clear()
  } else {
    reviewDialogItems.value = [...reviewDialogItems.value, ...uniqueNextItems]
  }

  for (const item of uniqueNextItems) {
    const columnIndex = reviewDialogColumnHeights[0] <= reviewDialogColumnHeights[1] ? 0 : 1
    reviewDialogColumns.value[columnIndex].push(item)
    await nextTick()
    const cardHeight = Math.max(reviewDialogCardRefs.get(item.id)?.getBoundingClientRect().height ?? 0, 128)
    reviewDialogColumnHeights[columnIndex] += cardHeight + (reviewDialogColumns.value[columnIndex].length > 1 ? 12 : 0)
  }
  isReviewDialogLoading.value = false
}

/** 快滑到评论弹窗底部时加载下一页，避免初始化阶段连续请求全部评论。 */
const handleReviewDialogScroll = (event: Event) => {
  const element = event.currentTarget as HTMLElement
  if (element.scrollHeight - element.scrollTop - element.clientHeight > 80) return
  void loadReviewDialogItems()
}

/** 记录评论卡片节点，用实际高度平衡新追加的评论。 */
const setReviewDialogCardRef = (reviewId: string) => (element: unknown) => {
  if (element instanceof HTMLElement) {
    reviewDialogCardRefs.set(reviewId, element)
  } else {
    reviewDialogCardRefs.delete(reviewId)
  }
}

/** 判断下拉选项是否为当前筛选值。 */
const isReviewFilterOptionSelected = (key: ReviewFilterKey, value: string) => {
  if (key === 'rating') return selectedReviewRating.value === value
  if (key === 'region') return selectedReviewRegion.value === value
  if (key === 'time') return selectedReviewTimeRange.value === value
  return selectedReviewReply.value === value
}

/** 评论卡片仅展示月日，筛选仍使用完整日期。 */
const formatReviewDate = (date: string) => date.slice(5)

/** 收集评论正文节点，并在渲染完成后判断是否超过 6 行。 */
const setReviewContentRef = (element: unknown, reviewId: string) => {
  if (!(element instanceof HTMLElement)) {
    reviewContentRefs.delete(reviewId)
    return
  }

  if (reviewContentRefs.get(reviewId) === element) return
  reviewContentRefs.set(reviewId, element)
  void nextTick(updateReviewContentOverflow)
}

/** 生成评论正文模板 ref 回调，避免模板内联参数丢失类型。 */
const bindReviewContentRef = (reviewId: string) => (element: unknown) => setReviewContentRef(element, reviewId)

/** 根据评论内容实际高度判断评论弹窗正文是否需要显示展开入口。 */
function updateReviewContentOverflow() {
  const overflowingIds: string[] = []
  const noEllipsisIds: string[] = []

  reviewContentRefs.forEach((element, reviewId) => {
    if (expandedReviewContentIds.value.includes(reviewId)) {
      if (overflowingReviewContentIds.value.includes(reviewId)) overflowingIds.push(reviewId)
      return
    }

    const contentElement = element.querySelector<HTMLElement>('[data-review-content-flow]') ?? element
    const isOverflowing = contentElement.scrollHeight > contentElement.clientHeight + 1
    if (isOverflowing) {
      overflowingIds.push(reviewId)

      const contentRect = contentElement.getBoundingClientRect()
      const lineHeight = Number.parseFloat(window.getComputedStyle(contentElement).lineHeight) || 20
      const lastVisibleLineTop = contentRect.bottom - lineHeight
      const replyMetaElements = contentElement.querySelectorAll<HTMLElement>('[data-review-reply-meta]')
      const hasReplyMetaOnLastLine = Array.from(replyMetaElements).some((replyMeta) => {
        const replyMetaRect = replyMeta.getBoundingClientRect()
        return (
          replyMetaRect.top >= lastVisibleLineTop - 1 &&
          replyMetaRect.top < lastVisibleLineTop + lineHeight - 1 &&
          replyMetaRect.bottom <= contentRect.bottom + 1
        )
      })

      if (hasReplyMetaOnLastLine) noEllipsisIds.push(reviewId)
    }
  })

  if (overflowingIds.join(',') !== overflowingReviewContentIds.value.join(',')) {
    overflowingReviewContentIds.value = overflowingIds
  }

  if (noEllipsisIds.join(',') !== reviewContentNoEllipsisIds.value.join(',')) {
    reviewContentNoEllipsisIds.value = noEllipsisIds
  }
}

/** 判断指定评论正文是否超过折叠行数。 */
const isReviewContentOverflowing = (reviewId: string) => overflowingReviewContentIds.value.includes(reviewId)

/** 判断折叠时第六行是否为开发者回复标题，避免省略号显示在标题行末尾。 */
const isReviewContentNoEllipsis = (reviewId: string) => reviewContentNoEllipsisIds.value.includes(reviewId)

/** 判断指定评论正文当前是否为展开状态。 */
const isReviewContentExpanded = (reviewId: string) => expandedReviewContentIds.value.includes(reviewId)

/** 单独切换当前评论正文，避免影响其他评论卡片。 */
const toggleReviewContent = (reviewId: string) => {
  expandedReviewContentIds.value = isReviewContentExpanded(reviewId)
    ? expandedReviewContentIds.value.filter((id) => id !== reviewId)
    : [...expandedReviewContentIds.value, reviewId]
  void nextTick(updateReviewContentOverflow)
}

/** 收集页面评分卡片正文节点，用实际高度判断是否超过两行。 */
const setPreviewReviewContentRef = (element: unknown, reviewId: string) => {
  if (!(element instanceof HTMLElement)) {
    previewReviewContentRefs.delete(reviewId)
    return
  }

  if (previewReviewContentRefs.get(reviewId) === element) return
  previewReviewContentRefs.set(reviewId, element)
  void nextTick(updatePreviewReviewOverflow)
}

/** 生成首页评论正文模板 ref 回调。 */
const bindPreviewReviewContentRef = (reviewId: string) => (element: unknown) =>
  setPreviewReviewContentRef(element, reviewId)

/** 页面评分卡片正文超过两行时才允许打开评论详情。 */
function updatePreviewReviewOverflow() {
  const overflowingIds: string[] = []

  previewReviewContentRefs.forEach((element, reviewId) => {
    const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight) || 20
    if (element.scrollHeight > lineHeight * 2 + 1) overflowingIds.push(reviewId)
  })

  if (overflowingIds.join(',') !== overflowingPreviewReviewIds.value.join(',')) {
    overflowingPreviewReviewIds.value = overflowingIds
  }
}

/** 判断页面评分卡片是否存在被两行截断的正文。 */
const isPreviewReviewOverflowing = (reviewId: string) => overflowingPreviewReviewIds.value.includes(reviewId)

/** 打开被截断评论的详情弹窗，并记录触发卡片用于关闭后恢复焦点。 */
const openPreviewReviewDialog = (review: ReviewDialogItem, event: Event) => {
  if (Date.now() - lastCarouselSwipeAt < 200 || !isPreviewReviewOverflowing(review.id)) return
  if (event.currentTarget instanceof HTMLElement) previewReviewDialogTrigger = event.currentTarget

  bodyOverflowBeforePreviewReviewDialog = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  isPreviewReviewDetailExpanded.value = false
  showPreviewReviewDetailToggle.value = false
  selectedPreviewReview.value = review
  void nextTick(updatePreviewReviewDetailOverflow)
}

/** 关闭评论详情弹窗并恢复页面滚动与触发卡片焦点。 */
const closePreviewReviewDialog = () => {
  if (!selectedPreviewReview.value) return
  document.body.style.overflow = bodyOverflowBeforePreviewReviewDialog
  selectedPreviewReview.value = null
  void nextTick(() => previewReviewDialogTrigger?.focus())
}

/** 页面卸载时兜底恢复评论详情弹窗锁定的页面滚动。 */
const restorePreviewReviewDialogBodyScroll = () => {
  if (!import.meta.client || !selectedPreviewReview.value) return
  document.body.style.overflow = bodyOverflowBeforePreviewReviewDialog
}

/** 详情正文最多展示六行，超出时复用统一的查看全部入口。 */
function updatePreviewReviewDetailOverflow() {
  const element = previewReviewDetailContentRef.value
  if (!element) return
  const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight) || 26
  showPreviewReviewDetailToggle.value = element.scrollHeight > lineHeight * 6 + 1
}

const moveBrandApps = (direction: -1 | 1) => {
  activeBrandAppIndex.value = Math.min(Math.max(activeBrandAppIndex.value + direction, 0), maxBrandAppIndex.value)
}

/** 展开版本记录时按接口总数补齐数据，收起时仍只展示首条。 */
const toggleVersionRecords = async () => {
  if (isVersionExpanded.value) {
    isVersionExpanded.value = false
    return
  }

  if (versionTotal.value > versionRecords.value.length) {
    const result = await getSoftwareReleaseNotes({
      marketId: softwareRouteParams.marketId,
      appBundleId: softwareRouteParams.appBundleId,
      pageIndex: 1,
      pageSize: versionTotal.value,
    }).catch(() => null)
    if (result) {
      versionRecords.value = result.items
      versionTotal.value = result.total
    }
  }
  isVersionExpanded.value = true
}

/** 记录轮播按下位置，Pointer Events 同时覆盖鼠标拖拽和触屏手势。 */
const startCarouselSwipe = (event: PointerEvent, target: CarouselSwipeTarget) => {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  carouselSwipeState = { pointerId: event.pointerId, startX: event.clientX, target }
}

/** 确认发生横向拖动后再捕获指针，避免普通点击被轮播容器截获。 */
const moveCarouselPointer = (event: PointerEvent) => {
  if (!carouselSwipeState || carouselSwipeState.pointerId !== event.pointerId) return
  if (Math.abs(event.clientX - carouselSwipeState.startX) < CAROUSEL_SWIPE_THRESHOLD) return
  if (event.currentTarget instanceof HTMLElement && !event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.setPointerCapture(event.pointerId)
  }
}

/** 横向移动超过阈值后切换一项，纵向手势仍交由页面正常滚动。 */
const endCarouselSwipe = (event: PointerEvent) => {
  if (!carouselSwipeState || carouselSwipeState.pointerId !== event.pointerId) return

  const { startX, target } = carouselSwipeState
  carouselSwipeState = null
  if (event.currentTarget instanceof HTMLElement && event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const distanceX = event.clientX - startX
  if (Math.abs(distanceX) < CAROUSEL_SWIPE_THRESHOLD) return

  lastCarouselSwipeAt = Date.now()

  const direction = (distanceX < 0 ? 1 : -1) * (isRtl.value ? -1 : 1)
  if (target === 'screenshot') moveScreenshot(direction as -1 | 1)
  if (target === 'review') moveReviews(direction as -1 | 1)
  if (target === 'brand') moveBrandApps(direction as -1 | 1)
}

/** 指针手势被浏览器取消时清理状态，避免下一次滑动误触发。 */
const cancelCarouselSwipe = (event: PointerEvent) => {
  if (carouselSwipeState?.pointerId === event.pointerId) carouselSwipeState = null
}

/** 记录标签横向拖拽起点，兼容鼠标、触控板和触屏手势。 */
const startSoftwareLabelsSwipe = (event: PointerEvent) => {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  const labelElement = (event.target as HTMLElement).closest<HTMLElement>('[data-software-label-index]')
  const labelIndex = Number(labelElement?.dataset.softwareLabelIndex)
  suppressNextSoftwareLabelClick = false
  softwareLabelsClickCandidate = Number.isInteger(labelIndex)
    ? softwareDetail.value?.softwareLabels?.[labelIndex]
    : undefined
  softwareLabelsSwipeState = {
    pointerId: event.pointerId,
    startX: event.clientX,
    lastX: event.clientX,
    moved: false,
  }
}

/** 根据指针位移直接更新标签容器的滚动位置，避免浏览器不触发原生横向滚动。 */
const moveSoftwareLabelsSwipe = (event: PointerEvent) => {
  if (!softwareLabelsSwipeState || softwareLabelsSwipeState.pointerId !== event.pointerId) return

  const labelsContainer = softwareLabelsRef.value
  if (!labelsContainer) return

  const deltaX = event.clientX - softwareLabelsSwipeState.lastX
  if (deltaX === 0) return

  if (Math.abs(event.clientX - softwareLabelsSwipeState.startX) > 5) {
    softwareLabelsSwipeState.moved = true
    if (!labelsContainer.hasPointerCapture(event.pointerId)) labelsContainer.setPointerCapture(event.pointerId)
  }
  labelsContainer.scrollLeft -= deltaX * (isRtl.value ? -1 : 1)
  softwareLabelsSwipeState.lastX = event.clientX
  event.preventDefault()
}

/** 结束标签拖拽并释放指针捕获。 */
const endSoftwareLabelsSwipe = (event: PointerEvent, cancelled = false) => {
  if (!softwareLabelsSwipeState || softwareLabelsSwipeState.pointerId !== event.pointerId) return

  const swipeState = softwareLabelsSwipeState
  const wasMoved = swipeState.moved
  suppressNextSoftwareLabelClick = !cancelled && wasMoved
  if (cancelled || wasMoved) softwareLabelsClickCandidate = undefined
  if (softwareLabelsRef.value?.hasPointerCapture(event.pointerId)) {
    softwareLabelsRef.value.releasePointerCapture(event.pointerId)
  }
  softwareLabelsSwipeState = null
}

/** 获取指定排行类型的 IOS 与 Android 数据，并缓存本次页面会话内的结果。 */
const loadSoftwareRanking = (type: SoftwareRankingType) => {
  const cachedRequest = softwareRankingRequests.get(type)
  if (cachedRequest) return cachedRequest

  const fetchRanking = SOFTWARE_RANKING_FETCHERS[type]
  const request = Promise.allSettled([fetchRanking(1), fetchRanking(2)]).then(([iosResult, androidResult]) => ({
    ios: iosResult.status === 'fulfilled' ? iosResult.value : { global: [], current: [] },
    android: androidResult.status === 'fulfilled' ? androidResult.value : { global: [], current: [] },
  }))

  softwareRankingRequests.set(type, request)
  return request
}

/** 将标签对应的排行结果加载到统一弹窗，并避免旧请求覆盖用户最新选择。 */
const openSoftwareRankingDialog = async (config: SoftwareRankingConfig) => {
  const requestSequence = ++softwareRankingRequestSequence
  softwareRankingType.value = config.type
  softwareRankingScope.value = config.scope
  softwareRankingData.value = EMPTY_SOFTWARE_RANKING_DATA()
  isSoftwareRankingDialogVisible.value = true
  isSoftwareRankingLoading.value = true

  const rankingData = await loadSoftwareRanking(config.type)
  if (requestSequence !== softwareRankingRequestSequence) return

  softwareRankingData.value[config.type] = rankingData
  isSoftwareRankingLoading.value = false
}

/** 将广告型软件标签返回首页对应模块，并保留旧站的锚点语义。 */
const openSoftwareHomeAnchor = (anchor: string) => {
  void navigateTo(`${localePath({ name: 'home' })}${anchor}`)
}

/** 通过容器事件委托处理标签点击，兼容标签容器的 pointer capture。 */
const handleSoftwareLabelsClick = (event: MouseEvent) => {
  if (suppressNextSoftwareLabelClick) {
    suppressNextSoftwareLabelClick = false
    softwareLabelsClickCandidate = undefined
    return
  }

  const labelElement = (event.target as HTMLElement).closest<HTMLElement>('[data-software-label-index]')
  const labelIndex = Number(labelElement?.dataset.softwareLabelIndex)
  const clickedLabel = Number.isInteger(labelIndex)
    ? softwareDetail.value?.softwareLabels?.[labelIndex]
    : softwareLabelsClickCandidate
  softwareLabelsClickCandidate = undefined
  if (clickedLabel) handleSoftwareLabelClick(clickedLabel)
}

/** 点击软件标签：排行标签打开排行弹窗，推荐/工具标签跳转首页锚点。 */
const handleSoftwareLabelClick = (label: SoftwareLabel) => {
  if (suppressNextSoftwareLabelClick) {
    suppressNextSoftwareLabelClick = false
    return
  }

  const homeAnchor = SOFTWARE_LABEL_HOME_ANCHORS[label.labelSource]
  if (homeAnchor) {
    openSoftwareHomeAnchor(homeAnchor)
    return
  }

  const rankingConfig = SOFTWARE_LABEL_RANKING_CONFIG[label.labelSource]
  if (rankingConfig) void openSoftwareRankingDialog(rankingConfig)
}

/** 根据按钮视口坐标计算浮窗位置，使浮窗脱离 Hero 裁切层显示在按钮下方。 */
const updateDownloadPopoverPosition = () => {
  const anchorRect = downloadPopoverAnchorRef.value?.getBoundingClientRect()
  if (!anchorRect) return
  downloadPopoverPosition.value = {
    top: anchorRect.bottom + 8,
    left: anchorRect.left + anchorRect.width / 2,
  }
}

/** 复用同一个浮窗，允许获取应用按钮和 APP ID 区域作为定位锚点。 */
const showDownloadPopover = (anchor: HTMLElement | null) => {
  if (!anchor) return
  if (downloadPopoverHideTimer) clearTimeout(downloadPopoverHideTimer)
  downloadPopoverAnchorRef.value = anchor
  isDownloadPopoverVisible.value = true
  updateDownloadPopoverPosition()
}

const hideDownloadPopover = () => {
  if (downloadPopoverHideTimer) clearTimeout(downloadPopoverHideTimer)
  isDownloadPopoverVisible.value = false
}

const scheduleHideDownloadPopover = () => {
  if (downloadPopoverHideTimer) clearTimeout(downloadPopoverHideTimer)
  downloadPopoverHideTimer = setTimeout(hideDownloadPopover, 160)
}

const keepDownloadPopoverVisible = () => {
  if (downloadPopoverHideTimer) clearTimeout(downloadPopoverHideTimer)
}

/** 将二维码地址复制到剪贴板。 */
const copySoftwareDownloadUrl = async () => {
  if (!softwareDownloadUrl.value) return
  let copied = false

  try {
    if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable')
    await navigator.clipboard.writeText(softwareDownloadUrl.value)
    copied = true
  } catch {
    const input = document.createElement('input')
    input.value = softwareDownloadUrl.value
    input.setAttribute('readonly', '')
    input.style.position = 'fixed'
    input.style.opacity = '0'
    document.body.appendChild(input)
    input.select()
    copied = document.execCommand('copy')
    document.body.removeChild(input)
  }

  if (typeof appToast === 'function') appToast(copied ? 1 : 0, copied ? t('82423') : t('89469'))
}

/** 打开统一下载弹窗，不附带下载量统计，供详情页多个入口复用。 */
const showSoftwareDownloadDialog = (title = softwareDetail.value?.name || '') => {
  downloadDialogTitle.value = title
  isHomeDownloadDialogVisible.value = true
}

/** 从软件信息的开发者入口打开企业详细信息弹窗。 */
const openDeveloperDownloadDialog = () => {
  showSoftwareDownloadDialog(t('88834'))
}

/** 点击获取应用时记录下载量并打开统一下载弹窗。 */
const openSoftwareDownloadDialog = () => {
  void addSoftwareDownloadCount({
    marketId: softwareRouteParams.marketId,
    appBundleId: softwareRouteParams.appBundleId,
  }).catch(() => null)
  showSoftwareDownloadDialog()
}

/** 关闭统一下载弹窗。 */
const closeHomeDownloadDialog = () => {
  isHomeDownloadDialogVisible.value = false
}

/** 关闭软件标签排行弹窗，并使未完成请求不再覆盖下一次标签选择。 */
const closeSoftwareRankingDialog = () => {
  softwareRankingRequestSequence += 1
  isSoftwareRankingDialogVisible.value = false
  isSoftwareRankingLoading.value = false
}
</script>

<template>
  <HeaderWikiBitPcTopNav :scroll-fade-distance="SOFTWARE_HEADER_FADE_DISTANCE" :use-white-logo="true" />
  <main class="min-h-[376px] w-full">
    <section
      class="software-details-hero relative h-[376px] w-full overflow-hidden text-white"
      :style="softwareHeroStyle"
    >
      <div class="software-details-hero-rotate" aria-hidden="true"></div>
      <div class="software-details-hero-blur" aria-hidden="true"></div>

      <div class="relative z-[3] mx-auto box-border h-full w-[1200px] pt-[94px]">
        <nav class="flex h-[22px] items-center gap-[4px] text-[14px] leading-[22px]" aria-label="Breadcrumb">
          <NuxtLink
            :to="localePath({ name: 'home' })"
            target="_blank"
            class="cursor-pointer text-white/60 hover:text-white"
          >
            {{ t('82520') }}
          </NuxtLink>
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/09/5e9d3fcb-db7f-490a-adc4-460f00bbb3a2.png"
            alt=""
            width="14"
            height="14"
            loading="eager"
            :fallback-src="false"
            class="shrink-0 rtl:rotate-180"
          />
          <template v-if="softwareBreadcrumbMiddle">
            <NuxtLink
              v-if="softwareBreadcrumbMiddle.href"
              :to="softwareBreadcrumbMiddle.href"
              external
              target="_blank"
              rel="noopener noreferrer"
              class="cursor-pointer text-white/60 hover:text-white"
            >
              {{ softwareBreadcrumbMiddle.label }}
            </NuxtLink>
            <span v-else class="text-white/60">{{ softwareBreadcrumbMiddle.label }}</span>
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/09/09/5e9d3fcb-db7f-490a-adc4-460f00bbb3a2.png"
              alt=""
              width="14"
              height="14"
              loading="eager"
              :fallback-src="false"
              class="shrink-0 rtl:rotate-180"
            />
          </template>
          <span aria-current="page">{{ t('89349') }}</span>
        </nav>

        <div class="mt-[36px] flex items-start gap-[20px]">
          <div class="software-details-hero-icon relative isolate size-[120px] shrink-0">
            <CommonImage
              :src="softwareIconUrl"
              :alt="softwareDetail?.name || ''"
              width="120"
              height="120"
              loading="eager"
              fetch-priority="high"
              class="overflow-hidden rounded-[8px]"
            />
          </div>

          <div class="flex min-w-0 flex-1 flex-col items-start gap-[16px]">
            <div class="flex flex-col items-start gap-[4px] whitespace-nowrap">
              <h1 class="text-[36px] leading-[50px] font-semibold">{{ softwareDetail?.name }}</h1>
              <p class="mt-[4px] text-[14px] leading-[22px]">{{ decodeHtmlText(softwareDetail?.title) }}</p>
            </div>

            <div
              v-if="softwareDetail?.softwareLabels?.length"
              ref="softwareLabelsRef"
              class="no-scrollbar flex w-full max-w-[1060px] cursor-grab touch-pan-y items-start gap-[10px] overflow-x-auto overscroll-x-contain select-none active:cursor-grabbing"
              @pointerdown="startSoftwareLabelsSwipe"
              @pointermove="moveSoftwareLabelsSwipe"
              @pointerup="endSoftwareLabelsSwipe"
              @pointercancel="endSoftwareLabelsSwipe($event, true)"
              @click="handleSoftwareLabelsClick"
            >
              <span
                v-for="(label, labelIndex) in softwareDetail.softwareLabels"
                :key="`${label.labelSource}-${label.labelName}`"
                :data-software-label-index="labelIndex"
                class="flex h-[28px] shrink-0 cursor-pointer items-center gap-[4px] rounded-[70px] bg-[rgba(227,228,242,0.12)] px-[10px] py-[4px] text-[14px] leading-[20px] whitespace-nowrap hover:bg-[rgba(227,228,242,0.24)]"
              >
                <CommonImage
                  :src="label.labelIcon"
                  :alt="label.labelName"
                  width="18"
                  object-fit="contain"
                  loading="eager"
                  :fallback-src="false"
                  class="shrink-0 overflow-hidden"
                />
                <span>{{ label.labelName }}</span>
              </span>
            </div>

            <button
              type="button"
              class="relative flex h-[38px] cursor-pointer items-center justify-center gap-[8px] rounded-[100px] border-0 bg-white/20 px-[16px] py-[8px] text-[14px] leading-[22px] font-semibold text-[#fff] hover:bg-white/30"
              @click="openSoftwareDownloadDialog"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/07/df4e6a1e-4641-46cf-a343-a15b2159dd1d.png"
                alt=""
                width="20"
                height="20"
                loading="eager"
                :fallback-src="false"
                class="shrink-0"
              />
              <span>{{ t('89350') }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <Teleport to="body">
      <div
        v-if="isDownloadPopoverVisible && softwareDownloadUrl"
        class="fixed z-[100] flex h-auto w-[197px] -translate-x-1/2 flex-col items-center rounded-[8px] bg-white py-[10px] text-[#1d2129] shadow-[0_8px_24px_rgba(0,0,0,0.1),0_16px_48px_rgba(0,0,0,0.1)]"
        :style="downloadPopoverStyle"
        @mouseenter="keepDownloadPopoverVisible"
        @mouseleave="scheduleHideDownloadPopover"
      >
        <div class="flex flex-col items-center gap-[2px] px-[24px] text-[14px] leading-[20px]">
          <div class="text-center">{{ t('89467') }}</div>
          <div class="text-center">{{ t('89468') }}</div>
        </div>
        <CommonQrCode :text="softwareDownloadUrl" :size="80" class="my-[20px] size-[80px] shrink-0" />
        <div class="flex h-[20px] w-full shrink-0 items-center gap-[8px] px-[24px]">
          <span
            dir="ltr"
            class="min-w-0 flex-1 truncate text-left text-[14px] leading-[20px] text-[#1d2129] rtl:text-right"
          >
            {{ softwareDownloadUrl }}
          </span>
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/08/1a4e893f-ab8c-48c3-ae8f-a4efbbd40e2e.png"
            alt=""
            width="14"
            height="14"
            loading="eager"
            :fallback-src="false"
            role="button"
            tabindex="0"
            aria-label="复制下载地址"
            class="shrink-0 cursor-pointer"
            @click.stop="copySoftwareDownloadUrl"
            @keydown.enter.stop="copySoftwareDownloadUrl"
          />
        </div>
      </div>
    </Teleport>

    <HomeDownloadDialog
      :visible="isHomeDownloadDialogVisible"
      :title="downloadDialogTitle"
      @close="closeHomeDownloadDialog"
    />

    <HomeSoftwareRankingDialog
      :visible="isSoftwareRankingDialogVisible"
      :loading="isSoftwareRankingLoading"
      :ranking-type="softwareRankingType"
      :ranking-scope="softwareRankingScope"
      :initial-device-type="initialSoftwareDeviceType"
      :active-data="softwareRankingData.active"
      :download-data="softwareRankingData.download"
      :popular-data="softwareRankingData.popular"
      :country-name="currentCountryName"
      @close="closeSoftwareRankingDialog"
    />

    <Teleport to="body">
      <div
        v-if="selectedScreenshot"
        class="fixed inset-0 z-[3000] flex items-center justify-center bg-[rgba(0,0,0,0.6)] p-[60px]"
        @click.self="closeScreenshotPreview"
      >
        <section role="dialog" aria-modal="true" aria-label="软件截图预览" class="relative">
          <CommonImage
            :src="selectedScreenshot"
            :alt="`${softwareDetail?.name || ''} 软件截图预览`"
            loading="eager"
            object-fit="contain"
            :fallback-src="false"
            class="screenshot-preview-image"
          />
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/09/742bd75e-e960-4b79-a1dd-c1e9921ea780.png"
            alt=""
            width="32"
            height="32"
            loading="eager"
            aria-label="关闭软件截图预览"
            :fallback-src="false"
            role="button"
            tabindex="0"
            class="absolute top-0 right-[-40px] shrink-0 cursor-pointer transition-opacity hover:opacity-75 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white rtl:right-auto rtl:left-[-40px]"
            @click.stop="closeScreenshotPreview"
            @keydown.enter.stop="closeScreenshotPreview"
            @keydown.space.prevent.stop="closeScreenshotPreview"
          />
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="isReviewDialogVisible"
        class="fixed inset-0 z-[3000] flex items-center justify-center bg-[rgba(9,20,51,0.48)] p-[24px]"
        @click.self="closeReviewDialog"
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="review-dialog-title"
          class="flex max-h-[min(720px,calc(100vh-48px))] w-[720px] max-w-full flex-col overflow-hidden rounded-[28px] bg-white"
          @click="activeReviewFilterMenu = null"
        >
          <header
            class="flex shrink-0 items-start justify-between border-b-[0.5px] border-[#0515240f] px-[32px] pt-[32px] pb-[16px]"
          >
            <h2 id="review-dialog-title" class="h-[32px] text-[20px] leading-[32px] font-semibold text-[#292a2e]">
              {{ t('89356') }}
            </h2>
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/09/09/742bd75e-e960-4b79-a1dd-c1e9921ea780.png"
              alt=""
              width="32"
              height="32"
              loading="eager"
              aria-label="关闭评分评论弹窗"
              :fallback-src="false"
              role="button"
              class="shrink-0 cursor-pointer"
              @click.stop="closeReviewDialog"
            />
          </header>

          <!-- 滚动条占 5px 宽，滚动方向一侧的内边距同步减 5px，避免 656px 头部内容被裁切 -->
          <div
            class="review-dialog-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto pt-[24px] pr-[27px] pb-[32px] pl-[32px] rtl:pr-[32px] rtl:pl-[27px]"
            @scroll.passive="handleReviewDialogScroll"
          >
            <div class="flex w-[656px] items-center gap-[80px]">
              <div class="flex h-[59px] w-[136px] shrink-0 flex-col items-center">
                <div dir="ltr" class="flex items-center gap-[7px]">
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/09/08/5ff5f24a-6b24-4ad8-8114-3d487cb1fc74.png"
                    alt=""
                    width="20"
                    height="20"
                    loading="eager"
                    :fallback-src="false"
                  />
                  <strong class="font-wiki-Medium text-[36px] leading-[44px] font-medium text-[#292a2e]">
                    {{ formatNumber(reviewDialogTotal) }}
                  </strong>
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/09/08/4965731d-7a91-4828-900c-f15a26df8c2a.png"
                    alt=""
                    width="20"
                    height="20"
                    loading="eager"
                    :fallback-src="false"
                  />
                </div>
                <span class="mt-[1px] text-[14px] leading-[20px] text-[#7d818a]">{{ t('89466') }}</span>
              </div>
              <div class="grid w-[440px] grid-cols-2 gap-x-[40px]">
                <div
                  v-for="(column, columnIndex) in reviewDialogRatingColumns"
                  :key="columnIndex"
                  class="grid grid-cols-[max-content_minmax(0,1fr)] content-start items-center gap-x-[20px] gap-y-[8px]"
                  :style="ratingLabelGridStyle"
                >
                  <template v-for="item in column" :key="item.label">
                    <span
                      :ref="setRatingLabelRef"
                      class="shrink-0 text-right text-[12px] leading-[17px] whitespace-nowrap text-[#292a2e] rtl:text-left"
                    >
                      {{ item.label }}
                    </span>
                    <div class="h-[4px] min-h-[4px] min-w-0 overflow-hidden rounded-[40px] bg-[#f0f1f2]">
                      <div class="h-full rounded-[40px] bg-[#0d3f63]" :style="{ width: item.width }"></div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="mt-[24px] flex h-[28px] items-center gap-[8px]">
              <div
                v-for="filter in reviewDialogFilters"
                :key="filter.label"
                class="relative inline-flex h-[28px] shrink-0"
              >
                <button
                  type="button"
                  :aria-expanded="activeReviewFilterMenu === filter.key"
                  :aria-controls="`review-filter-${filter.key}`"
                  class="flex h-full w-auto cursor-pointer items-center gap-[8px] rounded-[2px] border-0 bg-[#f8f8f8] px-[12px] py-[4px] text-[14px] leading-[20px] whitespace-nowrap text-[#292a2e] hover:bg-[#f0f1f2]"
                  @click.stop="toggleReviewFilterMenu(filter.key)"
                >
                  <span>{{ filter.label }}</span>
                  <CommonImage
                    src="https://resources1.interface003.com/web30/2026/09/09/f3f17148-056f-4271-b2a9-56319a0317f2.png"
                    alt=""
                    width="16"
                    height="16"
                    loading="eager"
                    :fallback-src="false"
                    class="shrink-0"
                  />
                </button>

                <div
                  v-if="activeReviewFilterMenu === filter.key"
                  :id="`review-filter-${filter.key}`"
                  role="listbox"
                  class="absolute top-[36px] left-0 z-[20] w-max min-w-full overflow-hidden rounded-[4px] border-[0.5px] border-solid border-[#0515240f] bg-white py-[4px] shadow-[0_8px_24px_rgba(5,21,36,0.12)] rtl:right-0 rtl:left-auto"
                  @click.stop
                >
                  <button
                    v-for="option in filter.options"
                    :key="option.value"
                    type="button"
                    role="option"
                    :aria-selected="isReviewFilterOptionSelected(filter.key, option.value)"
                    class="flex h-[32px] w-full cursor-pointer items-center border-0 bg-white px-[12px] text-left text-[13px] leading-[20px] whitespace-nowrap hover:bg-[#f8f8f8] rtl:text-right"
                    :class="
                      isReviewFilterOptionSelected(filter.key, option.value)
                        ? 'font-medium text-[#0d3f63]'
                        : 'text-[#292a2e]'
                    "
                    @click.stop="selectReviewFilter(filter.key, option.value)"
                  >
                    <CommonImage
                      v-if="filter.key === 'region' && option.flag"
                      :src="option.flag"
                      :alt="option.label"
                      width="18"
                      height="auto"
                      loading="eager"
                      :fallback-src="false"
                      object-fit="contain"
                      class="mr-[8px] max-h-[18px] shrink-0 rounded-[2px] rtl:mr-0 rtl:ml-[8px]"
                    />
                    {{ option.label }}
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="isReviewDialogInitialLoading && !reviewDialogItems.length"
              class="mt-[24px] flex h-[160px] items-center justify-center"
              role="status"
              aria-label="正在加载评价"
            >
              <div class="review-dialog-loading" aria-hidden="true">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
            <div v-else-if="filteredReviewDialogItems.length" class="mt-[24px] grid grid-cols-2 gap-[12px]">
              <div
                v-for="(column, columnIndex) in reviewDialogColumns"
                :key="columnIndex"
                class="flex min-w-0 flex-col gap-[12px]"
              >
                <article
                  v-for="review in column"
                  :key="review.id"
                  :ref="setReviewDialogCardRef(review.id)"
                  class="w-full min-w-0 rounded-[8px] bg-[#f8f8f8] p-[16px]"
                >
                  <h3 class="text-[16px] leading-[20px] font-semibold text-[#292a2e]">
                    <bdi>{{ review.title }}</bdi>
                  </h3>
                  <div class="mt-[8px] flex items-center gap-[6px] text-[12px] leading-[16px] whitespace-nowrap">
                    <span class="font-medium text-[#e06c00]">{{ review.rating }}</span>
                    <span class="h-[12px] w-[0.5px] shrink-0 bg-[#0b122824]"></span>
                    <CommonImage
                      :src="review.flag"
                      :alt="review.region"
                      width="22"
                      height="auto"
                      loading="eager"
                      class="shrink-0 rounded-[1px]"
                    />
                    <span class="h-[12px] w-[0.5px] shrink-0 bg-[#0b122824]"></span>
                    <span class="text-[#7d818a]">{{ formatReviewDate(review.date) }}</span>
                    <span class="h-[12px] w-[0.5px] shrink-0 bg-[#0b122824]"></span>
                    <span dir="auto" class="min-w-0 truncate text-[#7d818a]">{{ review.author }}</span>
                  </div>
                  <div :id="`review-content-${review.id}`" :ref="bindReviewContentRef(review.id)" class="mt-[8px]">
                    <template v-if="!isReviewContentExpanded(review.id)">
                      <p
                        data-review-content-flow
                        class="review-content-collapsed-flow text-[14px] leading-[20px] text-[#7d818a]"
                        :class="isReviewContentNoEllipsis(review.id) ? 'review-content-no-ellipsis' : ''"
                      >
                        <span
                          ><bdi>{{ review.content }}</bdi></span
                        >
                        <template v-for="(reply, replyIndex) in review.replies" :key="`${reply.date}-${replyIndex}`">
                          <br />
                          <span data-review-reply-meta class="inline-flex items-center gap-[6px] pt-[8px] pb-[8px]">
                            <strong class="text-[14px] leading-[20px] font-medium text-[#292a2e]">
                              {{ t('89373') }}
                            </strong>
                            <span class="text-[12px] leading-[16px] text-[#7d818a]">
                              {{ reply.date || review.date }}
                            </span>
                          </span>
                          <br />
                          <span class="text-[14px] leading-[20px] text-[#7d818a]"
                            ><bdi>{{ reply.content }}</bdi></span
                          >
                        </template>
                      </p>
                    </template>
                    <template v-else>
                      <p class="text-[14px] leading-[20px] text-[#7d818a]">
                        <bdi>{{ review.content }}</bdi>
                      </p>
                      <div
                        v-for="(reply, replyIndex) in review.replies"
                        :key="`${reply.date}-${replyIndex}`"
                        class="mt-[8px]"
                      >
                        <div class="flex items-center gap-[6px]">
                          <strong class="text-[14px] leading-[20px] font-medium text-[#292a2e]">
                            {{ t('89373') }}
                          </strong>
                          <span class="text-[12px] leading-[16px] text-[#7d818a]">
                            {{ reply.date || review.date }}
                          </span>
                        </div>
                        <p class="mt-[8px] text-[14px] leading-[20px] text-[#7d818a]">
                          <bdi>{{ reply.content }}</bdi>
                        </p>
                      </div>
                    </template>
                  </div>
                  <button
                    v-if="isReviewContentOverflowing(review.id)"
                    type="button"
                    :aria-expanded="isReviewContentExpanded(review.id)"
                    :aria-controls="`review-content-${review.id}`"
                    class="mt-[6px] flex w-full cursor-pointer items-center justify-center gap-[4px] border-0 bg-transparent text-[14px] leading-[22px] text-[#0d3f63] hover:opacity-80"
                    @click="toggleReviewContent(review.id)"
                  >
                    <span>{{ isReviewContentExpanded(review.id) ? t('83802') : t('84516') }}</span>
                    <CommonImage
                      :src="
                        isReviewContentExpanded(review.id)
                          ? 'https://resources1.interface003.com/web30/2026/09/08/574c190f-a51c-4b95-bd3e-0115ed9d6c4c.png'
                          : 'https://resources1.interface003.com/web30/2026/09/08/c1044224-bd00-4c79-bae0-61869eb67581.png'
                      "
                      alt=""
                      width="20"
                      height="20"
                      loading="eager"
                      :fallback-src="false"
                    />
                  </button>
                </article>
              </div>
            </div>
            <div v-else-if="!isReviewDialogLoading" class="flex justify-center py-[80px]">
              <div class="text-center">
                <CommonImage
                  src="https://resources1.interface003.com/web20/img/service/no-data.png"
                  alt=""
                  loading="eager"
                />
                <p class="text-[14px] leading-[20px] text-[#86909c]">{{ t('83714') }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="selectedPreviewReview"
        class="fixed inset-0 z-[3000] flex items-center justify-center bg-[rgba(9,20,51,0.48)] p-[24px]"
        @click.self="closePreviewReviewDialog"
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="preview-review-dialog-title"
          class="flex max-h-[min(720px,calc(100vh-48px))] w-[720px] max-w-full flex-col overflow-hidden rounded-[28px] bg-white"
        >
          <header class="shrink-0 bg-white px-[32px] pt-[32px]">
            <div class="flex items-center gap-[8px] border-b-[0.5px] border-[#0515240f] pb-[16px]">
              <h2
                id="preview-review-dialog-title"
                class="min-w-0 flex-1 truncate text-[20px] leading-[28px] font-semibold text-[#292a2e]"
              >
                <bdi>{{ selectedPreviewReview.title }}</bdi>
              </h2>
              <button
                type="button"
                aria-label="关闭评论详情弹窗"
                class="size-[32px] shrink-0 cursor-pointer border-0 bg-transparent p-0 hover:opacity-80"
                @click="closePreviewReviewDialog"
              >
                <CommonImage
                  src="https://resources1.interface003.com/web30/2026/09/09/742bd75e-e960-4b79-a1dd-c1e9921ea780.png"
                  alt=""
                  width="32"
                  height="32"
                  loading="eager"
                  :fallback-src="false"
                />
              </button>
            </div>
          </header>

          <div class="review-dialog-scrollbar min-h-0 overflow-x-hidden overflow-y-auto px-[32px] pt-[20px] pb-[32px]">
            <div class="flex items-center gap-[6px] text-[14px] leading-[20px] whitespace-nowrap">
              <span class="font-medium text-[#e06c00]">{{ selectedPreviewReview.rating }}</span>
              <span class="h-[12px] w-[0.5px] shrink-0 bg-[#0b122824]"></span>
              <CommonImage
                v-if="selectedPreviewReview.flag"
                :src="selectedPreviewReview.flag"
                :alt="selectedPreviewReview.region"
                width="22"
                height="auto"
                loading="eager"
                :fallback-src="false"
                class="shrink-0 rounded-[1px]"
              />
              <span class="h-[12px] w-[0.5px] shrink-0 bg-[#0b122824]"></span>
              <span class="text-[#7d818a]">{{ formatReviewDate(selectedPreviewReview.date) }}</span>
              <span class="h-[12px] w-[0.5px] shrink-0 bg-[#0b122824]"></span>
              <span dir="auto" class="min-w-0 truncate text-[#7d818a]">{{ selectedPreviewReview.author }}</span>
            </div>
            <p
              ref="previewReviewDetailContentRef"
              class="mt-[12px] text-[16px] leading-[26px] text-[#7d818a]"
              :class="isPreviewReviewDetailExpanded ? '' : 'preview-review-detail-content-collapsed'"
            >
              <bdi>{{ selectedPreviewReview.content }}</bdi>
            </p>
            <button
              v-if="showPreviewReviewDetailToggle"
              type="button"
              class="mx-auto mt-[12px] flex cursor-pointer items-center gap-[4px] border-0 bg-transparent text-[14px] leading-[22px] text-[#0d3f63] hover:opacity-80"
              @click="isPreviewReviewDetailExpanded = !isPreviewReviewDetailExpanded"
            >
              <span>{{ isPreviewReviewDetailExpanded ? t('83802') : t('84516') }}</span>
              <CommonImage
                :src="
                  isPreviewReviewDetailExpanded
                    ? 'https://resources1.interface003.com/web30/2026/09/08/574c190f-a51c-4b95-bd3e-0115ed9d6c4c.png'
                    : 'https://resources1.interface003.com/web30/2026/09/08/c1044224-bd00-4c79-bae0-61869eb67581.png'
                "
                alt=""
                width="20"
                height="20"
                loading="eager"
                :fallback-src="false"
              />
            </button>
          </div>
        </section>
      </div>
    </Teleport>

    <section
      class="software-details-info mx-auto mt-[20px] flex h-[92px] w-[1200px] items-center gap-[10px] rounded-[6px] py-[20px]"
      :aria-label="t('89362')"
    >
      <div class="flex h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-[10px]">
        <div class="flex items-center gap-[2px] text-[14px] leading-[20px] text-[#7d818a]">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/07/96e047e2-fdcb-471f-98e0-31a6bb44e62f.png"
            alt=""
            width="16"
            height="16"
            loading="eager"
            :fallback-src="false"
            class="shrink-0"
          />
          <span>{{ t('89351') }}</span>
        </div>
        <span class="text-[16px] leading-[22px] font-medium" :class="softwareStatusClass">
          {{ softwareStatusLabel }}
        </span>
      </div>

      <div class="h-[50px] w-0 shrink-0" aria-hidden="true">
        <div class="h-full w-[0.5px] -translate-x-[0.25px] bg-[#0b122824]"></div>
      </div>

      <div class="flex h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-[10px]">
        <span class="text-[14px] leading-[20px] text-[#7d818a]">
          {{ t('89354', [formatNumber(softwareDetail?.ratingCount)]) }}
        </span>
        <div
          dir="ltr"
          class="flex items-start"
          :aria-label="`评分 ${ratingValue} 星，共 ${formatNumber(softwareDetail?.ratingCount)} 个评分`"
        >
          <span
            v-for="(star, index) in ratingStars"
            :key="index"
            class="relative flex size-[18px] shrink-0 items-start"
          >
            <CommonImage
              :src="star === 'full' ? FULL_STAR_ICON : EMPTY_STAR_ICON"
              alt=""
              width="18"
              height="18"
              loading="eager"
              :fallback-src="false"
              class="size-[18px]"
            />
            <span
              v-if="star === 'half'"
              dir="ltr"
              class="pointer-events-none absolute top-0 left-0 z-[2] flex h-[18px] w-[9px] items-start justify-start overflow-hidden"
            >
              <CommonImage
                :src="FULL_STAR_ICON"
                alt=""
                width="18"
                height="18"
                loading="eager"
                :fallback-src="false"
                class="size-[18px] max-w-none shrink-0"
              />
            </span>
          </span>
        </div>
      </div>

      <div class="h-[50px] w-0 shrink-0" aria-hidden="true">
        <div class="h-full w-[0.5px] -translate-x-[0.25px] bg-[#0b122824]"></div>
      </div>

      <div class="flex h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-[10px]">
        <span class="text-[14px] leading-[20px] text-[#7d818a]">{{ t('89347') }}</span>
        <span class="text-[16px] leading-[22px] font-medium text-[#292a2e]">
          {{ softwareDetail?.sizes || '--' }}
        </span>
      </div>

      <div class="h-[50px] w-0 shrink-0" aria-hidden="true">
        <div class="h-full w-[0.5px] -translate-x-[0.25px] bg-[#0b122824]"></div>
      </div>

      <div
        ref="appIdRef"
        class="flex h-[52px] min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-[10px]"
        @mouseenter="showDownloadPopover(appIdRef)"
        @mouseleave="scheduleHideDownloadPopover"
      >
        <div class="flex items-center gap-[2px] text-[14px] leading-[20px] text-[#7d818a]">
          <span>APP ID</span>
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/07/77095657-1ce6-42a7-8f4f-9e3af87c9255.png"
            alt=""
            width="16"
            height="16"
            loading="eager"
            :fallback-src="false"
            class="shrink-0"
          />
        </div>
        <span class="max-w-full truncate text-[16px] leading-[22px] font-medium text-[#0d3f63]">
          {{ softwareDetail?.appBundleId || '--' }}
        </span>
      </div>

      <div class="h-[50px] w-0 shrink-0" aria-hidden="true">
        <div class="h-full w-[0.5px] -translate-x-[0.25px] bg-[#0b122824]"></div>
      </div>

      <div class="flex h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-[10px]">
        <span class="text-[14px] leading-[20px] text-[#7d818a]">{{ t('89355') }}</span>
        <span class="text-[16px] leading-[22px] font-medium text-[#292a2e]">
          {{ softwareDetail?.shortCompatibility || softwareDetail?.compatibility || '--' }}
        </span>
      </div>
    </section>

    <section
      v-if="screenshotItems.length"
      class="group relative left-1/2 mt-[20px] h-[492px] w-[1260px] -translate-x-1/2 bg-transparent rtl:right-1/2 rtl:left-auto rtl:translate-x-1/2"
      aria-label="软件截图"
    >
      <div
        class="mx-auto h-full w-[1200px] cursor-grab touch-pan-y overflow-hidden rounded-[16px] select-none active:cursor-grabbing"
        @pointerdown="startCarouselSwipe($event, 'screenshot')"
        @pointermove="moveCarouselPointer"
        @pointerup="endCarouselSwipe"
        @pointercancel="cancelCarouselSwipe"
        @dragstart.prevent
      >
        <div
          ref="screenshotTrackRef"
          class="flex h-full gap-[16px] transition-transform duration-300 ease-out"
          :style="screenshotTrackStyle"
        >
          <CommonImage
            v-for="screenshot in screenshotItems"
            :key="screenshot"
            :src="screenshot"
            :alt="`${softwareDetail?.name || ''} 软件截图`"
            height="492"
            object-fit="contain"
            loading="eager"
            :fallback-src="false"
            role="button"
            tabindex="0"
            class="h-[492px] w-auto shrink-0 cursor-pointer overflow-hidden rounded-[16px] transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white"
            @load="updateScreenshotWidths"
            @click="openScreenshotPreview(screenshot, $event)"
            @keydown.enter="openScreenshotPreview(screenshot, $event)"
            @keydown.space.prevent="openScreenshotPreview(screenshot, $event)"
          />
        </div>
      </div>

      <template v-if="maxScreenshotIndex > 0">
        <button
          v-show="activeScreenshotIndex > 0"
          type="button"
          aria-label="上一张截图"
          class="pointer-events-none absolute top-1/2 left-[10px] z-[2] -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 hover:brightness-90 focus-visible:pointer-events-auto focus-visible:opacity-100 rtl:right-[10px] rtl:left-auto"
          @click="moveScreenshot(-1)"
        >
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/07/0afaca60-bf8f-4d04-9230-08da7c33858f.png"
            alt=""
            width="20"
            height="34"
            loading="eager"
            :fallback-src="false"
            class="rotate-180 rtl:rotate-0"
          />
        </button>
        <button
          v-show="activeScreenshotIndex < maxScreenshotIndex"
          type="button"
          aria-label="下一张截图"
          class="pointer-events-none absolute top-1/2 right-[10px] z-[2] -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 hover:brightness-90 focus-visible:pointer-events-auto focus-visible:opacity-100 rtl:right-auto rtl:left-[10px]"
          @click="moveScreenshot(1)"
        >
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/09/07/0afaca60-bf8f-4d04-9230-08da7c33858f.png"
            alt=""
            width="20"
            height="34"
            loading="eager"
            :fallback-src="false"
            class="rtl:rotate-180"
          />
        </button>
      </template>
    </section>

    <div class="mx-auto mt-[40px] flex w-[1200px] flex-col gap-[40px]">
      <section
        v-if="descriptionParagraphs.length"
        class="border-b-[0.5px] border-[#0b122824] pb-[40px]"
        aria-label="应用介绍"
      >
        <div
          ref="descriptionContentRef"
          class="flex flex-col gap-[20px] overflow-hidden text-[16px] leading-[24px] text-[#292a2e] transition-[max-height] duration-300"
          :style="{ maxHeight: isDescriptionExpanded ? 'none' : `${descriptionCollapsedHeight}px` }"
        >
          <p v-for="(paragraph, index) in descriptionParagraphs" :key="index" class="whitespace-pre-line">
            <bdi>{{ paragraph }}</bdi>
          </p>
        </div>
        <button
          v-if="showDescriptionToggle"
          type="button"
          class="mx-auto mt-[24px] flex cursor-pointer items-center gap-[4px] border-0 bg-transparent text-[14px] leading-[22px] text-[#0d3f63]"
          @click="isDescriptionExpanded = !isDescriptionExpanded"
        >
          <span>{{ isDescriptionExpanded ? t('83802') : t('84516') }}</span>
          <CommonImage
            :src="
              isDescriptionExpanded
                ? 'https://resources1.interface003.com/web30/2026/09/08/574c190f-a51c-4b95-bd3e-0115ed9d6c4c.png'
                : 'https://resources1.interface003.com/web30/2026/09/08/c1044224-bd00-4c79-bae0-61869eb67581.png'
            "
            alt=""
            width="20"
            height="20"
            loading="eager"
            :fallback-src="false"
          />
        </button>
      </section>

      <section
        v-if="commentTotal > 0 || reviewItems.length"
        class="border-b-[0.5px] border-[#0b122824] pb-[40px]"
        aria-labelledby="reviews-title"
      >
        <div class="flex items-center justify-between">
          <h2 id="reviews-title" class="text-[24px] leading-[32px] font-semibold text-[#292a2e]">{{ t('89356') }}</h2>
          <button
            ref="reviewDialogTriggerRef"
            type="button"
            :aria-label="t('84516')"
            class="cursor-pointer border-0 bg-transparent hover:opacity-70"
            @click="openReviewDialog"
          >
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/09/07/dd670590-6bb6-4e33-add2-7d1105c39f17.png"
              alt=""
              width="20"
              height="20"
              loading="eager"
              :fallback-src="false"
              class="rtl:rotate-180"
            />
          </button>
        </div>

        <div class="mt-[24px] flex items-center gap-[80px]">
          <div class="flex shrink-0 flex-col items-center px-[12px]">
            <div dir="ltr" class="flex items-center gap-[7px]">
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/08/5ff5f24a-6b24-4ad8-8114-3d487cb1fc74.png"
                alt=""
                width="20"
                height="20"
                loading="eager"
                :fallback-src="false"
              />
              <strong class="font-wiki-Medium text-[36px] leading-[44px] font-medium text-[#292a2e]">
                {{ formatNumber(commentTotal) }}
              </strong>
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/08/4965731d-7a91-4828-900c-f15a26df8c2a.png"
                alt=""
                width="20"
                height="20"
                loading="eager"
                :fallback-src="false"
              />
            </div>
            <span class="mt-[1px] text-[14px] text-[#7d818a]">{{ t('89466') }}</span>
          </div>
          <div class="grid min-w-0 flex-1 grid-cols-3 gap-x-[32px]">
            <div
              v-for="(column, columnIndex) in ratingColumns"
              :key="columnIndex"
              class="grid grid-cols-[max-content_minmax(0,1fr)] content-start items-center gap-x-[20px] gap-y-[16px]"
              :style="ratingLabelGridStyle"
            >
              <template v-for="item in column" :key="item.label">
                <span
                  :ref="setRatingLabelRef"
                  class="shrink-0 text-right text-[12px] leading-[17px] whitespace-nowrap text-[#292a2e] rtl:text-left"
                >
                  {{ item.label }}
                </span>
                <div class="h-[4px] min-h-[4px] min-w-0 overflow-hidden rounded-[40px] bg-[#f0f1f2]">
                  <div class="h-full rounded-[40px] bg-[#112D73]" :style="{ width: item.width }"></div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="group relative mt-[24px] ml-[-30px] w-[1260px] rtl:mr-[-30px] rtl:ml-0">
          <div
            class="relative left-[30px] w-[1200px] cursor-grab touch-pan-y overflow-hidden rounded-[8px] select-none active:cursor-grabbing rtl:right-[30px] rtl:left-auto"
            @pointerdown="startCarouselSwipe($event, 'review')"
            @pointermove="moveCarouselPointer"
            @pointerup="endCarouselSwipe"
            @pointercancel="cancelCarouselSwipe"
            @dragstart.prevent
          >
            <div
              class="flex items-stretch gap-[20px] transition-transform duration-300 ease-out"
              :style="reviewTrackStyle"
            >
              <article
                v-for="review in reviewItems"
                :key="review.id"
                class="shrink-0 rounded-[8px] bg-[#f8f8f8] p-[16px]"
                :class="
                  isPreviewReviewOverflowing(review.id)
                    ? 'cursor-pointer hover:bg-[#f0f1f2] focus-visible:outline-[2px] focus-visible:outline-offset-[-2px] focus-visible:outline-[#0d3f63]'
                    : ''
                "
                :style="reviewItemStyle"
                :role="isPreviewReviewOverflowing(review.id) ? 'button' : undefined"
                :tabindex="isPreviewReviewOverflowing(review.id) ? 0 : undefined"
                :aria-haspopup="isPreviewReviewOverflowing(review.id) ? 'dialog' : undefined"
                :aria-label="isPreviewReviewOverflowing(review.id) ? `查看${review.title}评论详情` : undefined"
                @click="openPreviewReviewDialog(review, $event)"
                @keydown.enter="openPreviewReviewDialog(review, $event)"
                @keydown.space.prevent="openPreviewReviewDialog(review, $event)"
              >
                <h3 class="text-[16px] leading-[20px] font-semibold text-[#292a2e]">
                  <bdi>{{ review.title }}</bdi>
                </h3>
                <div class="mt-[8px] flex items-center gap-[6px] text-[12px] leading-[16px]">
                  <span class="font-medium text-[#e06c00]">{{ review.rating }}</span>
                  <span class="h-[12px] w-[0.5px] bg-[#0b122824]"></span>
                  <CommonImage
                    v-if="review.flag"
                    :src="review.flag"
                    :alt="review.region"
                    width="22"
                    height="auto"
                    loading="eager"
                    :fallback-src="false"
                    class="shrink-0 rounded-[1px]"
                  />
                  <span class="h-[12px] w-[0.5px] bg-[#0b122824]"></span>
                  <span class="text-[#7d818a]">{{ formatReviewDate(review.date) }}</span>
                  <span class="h-[12px] w-[0.5px] bg-[#0b122824]"></span>
                  <span dir="auto" class="text-[#7d818a]">{{ review.author }}</span>
                </div>
                <p
                  :ref="bindPreviewReviewContentRef(review.id)"
                  class="preview-review-content-collapsed mt-[8px] text-[14px] leading-[20px] text-[#7d818a]"
                >
                  <bdi>{{ review.content }}</bdi>
                </p>
              </article>
            </div>
          </div>

          <template v-if="maxReviewIndex > 0">
            <button
              v-show="activeReviewIndex > 0"
              type="button"
              aria-label="上一条评论"
              class="pointer-events-none absolute top-1/2 left-[10px] z-[2] -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 hover:brightness-90 focus-visible:pointer-events-auto focus-visible:opacity-100 rtl:right-[10px] rtl:left-auto"
              @click="moveReviews(-1)"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/07/0afaca60-bf8f-4d04-9230-08da7c33858f.png"
                alt=""
                width="20"
                height="34"
                loading="eager"
                :fallback-src="false"
                class="rotate-180 hover:brightness-90 rtl:rotate-0"
              />
            </button>
            <button
              v-show="activeReviewIndex < maxReviewIndex"
              type="button"
              aria-label="下一条评论"
              class="pointer-events-none absolute top-1/2 right-[10px] z-[2] -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 hover:brightness-90 focus-visible:pointer-events-auto focus-visible:opacity-100 rtl:right-auto rtl:left-[10px]"
              @click="moveReviews(1)"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/07/0afaca60-bf8f-4d04-9230-08da7c33858f.png"
                alt=""
                width="20"
                height="34"
                loading="eager"
                :fallback-src="false"
                class="hover:brightness-90 rtl:rotate-180"
              />
            </button>
          </template>
        </div>
      </section>

      <section class="border-b-[0.5px] border-[#0b122824] pb-[40px]" aria-labelledby="software-info-title">
        <h2 id="software-info-title" class="text-[24px] leading-[32px] font-semibold text-[#292a2e]">
          {{ t('89362') }}
        </h2>
        <div class="mt-[24px] grid grid-cols-3 gap-[20px]">
          <div v-for="(group, groupIndex) in softwareInfoGroups" :key="groupIndex" class="flex flex-col gap-[24px]">
            <div v-for="item in group" :key="item.label" class="flex flex-col gap-[8px]">
              <span class="text-[13px] leading-[20px] text-[#7d818a]">{{ item.label }}</span>
              <div class="flex items-center gap-[4px] text-[14px] leading-[22px] font-medium text-[#292a2e]">
                <button
                  v-if="item.action === 'download'"
                  type="button"
                  class="flex cursor-pointer items-center gap-[4px] border-0 bg-transparent p-0 text-start text-[14px] leading-[22px] font-medium text-[#292a2e] hover:opacity-80"
                  @click="openDeveloperDownloadDialog"
                >
                  <CommonImage
                    v-if="item.icon"
                    :src="softwareIconUrl"
                    alt=""
                    width="16"
                    height="16"
                    loading="eager"
                    :fallback-src="false"
                    class="rounded-[2px]"
                  />
                  <span dir="auto">{{ item.value }}</span>
                </button>
                <template v-else>
                  <CommonImage
                    v-if="item.icon"
                    :src="softwareIconUrl"
                    alt=""
                    width="16"
                    height="16"
                    loading="eager"
                    :fallback-src="false"
                    class="rounded-[2px]"
                  />
                  <NuxtLink
                    v-if="item.href"
                    :to="item.href"
                    external
                    target="_blank"
                    rel="noopener noreferrer"
                    class="cursor-pointer"
                    dir="auto"
                  >
                    {{ item.value }}
                  </NuxtLink>
                  <span v-else dir="auto">{{ item.value }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="versionRecords.length"
        class="border-b-[0.5px] border-[#0b122824] pb-[40px]"
        aria-labelledby="version-title"
      >
        <h2 id="version-title" class="text-[24px] leading-[32px] font-semibold text-[#292a2e]">
          {{ t('89368') }}
        </h2>
        <div class="mt-[24px] flex gap-[12px]">
          <div class="relative w-[16px] shrink-0">
            <div
              class="absolute top-[10px] bottom-[10px] left-[7.5px] w-[1px] bg-[#dfe1e5] rtl:right-[7.5px] rtl:left-auto"
            ></div>
          </div>
          <div class="flex min-w-0 flex-1 flex-col gap-[24px]">
            <article
              v-for="(record, index) in visibleVersions"
              :key="`${record.version}-${record.releaseTime}`"
              class="relative flex flex-col gap-[10px]"
            >
              <CommonImage
                :src="
                  index === 0
                    ? 'https://resources1.interface003.com/web30/2026/09/08/cb3bfc4a-fa83-4c38-8d16-715d948684c5.png'
                    : 'https://resources1.interface003.com/web30/2026/09/08/0d84254e-df50-4ade-b29e-91ca98f9543f.png'
                "
                alt=""
                width="10"
                height="10"
                loading="eager"
                :fallback-src="false"
                class="absolute top-[4px] left-[-25px] rtl:right-[-25px] rtl:left-auto"
              />
              <p class="text-[14px] leading-[18px] text-[#7d818a]">
                <bdi>{{ formatVersionDate(record.releaseTime) }}</bdi>
              </p>
              <div class="flex flex-col gap-[5px]">
                <h3 class="text-[16px] leading-[20px] font-medium text-[#292a2e]">
                  <bdi>{{ record.name }}</bdi>
                </h3>
                <p class="text-[14px] leading-[18px] text-[#7d818a]">
                  {{ t('89369') }}:<bdi>{{ record.version }}</bdi>
                </p>
              </div>
              <div class="flex flex-col gap-[5px]">
                <h4 class="text-[16px] leading-[20px] font-medium text-[#292a2e]">{{ t('89370') }}</h4>
                <p class="text-[14px] leading-[20px] whitespace-pre-line text-[#7d818a]">
                  <bdi>{{ record.releaseNote }}</bdi>
                </p>
              </div>
            </article>
          </div>
        </div>
        <button
          v-if="showVersionToggle"
          type="button"
          class="mx-auto mt-[24px] flex cursor-pointer items-center gap-[4px] border-0 bg-transparent text-[14px] leading-[22px] text-[#0d3f63]"
          @click="toggleVersionRecords"
        >
          <span>{{ isVersionExpanded ? t('83802') : t('84516') }}</span>
          <CommonImage
            :src="
              isVersionExpanded
                ? 'https://resources1.interface003.com/web30/2026/09/08/574c190f-a51c-4b95-bd3e-0115ed9d6c4c.png'
                : 'https://resources1.interface003.com/web30/2026/09/08/c1044224-bd00-4c79-bae0-61869eb67581.png'
            "
            alt=""
            width="20"
            height="20"
            loading="eager"
            :fallback-src="false"
          />
        </button>
      </section>

      <section v-if="hasBrandApps" class="pb-[40px]" aria-labelledby="brand-apps-title">
        <h2 id="brand-apps-title" class="text-[24px] leading-[32px] font-semibold text-[#292a2e]">
          {{ t('89372') }}
        </h2>
        <div v-if="platformItems.length > 1" class="relative mt-[24px] flex h-[36px] gap-[8px] overflow-visible">
          <button
            v-for="platform in platformItems"
            :key="platform"
            type="button"
            class="cursor-pointer rounded-[2px] px-[12px] py-[8px] text-[13px] leading-[20px]"
            :class="
              activePlatform === platform
                ? 'bg-[#112d73] font-medium text-white'
                : 'border-[0.5px] border-[#0b122824] bg-white text-[#292a2e]'
            "
            @click="activePlatform = platform"
          >
            {{ t('89336', [platform === 'ios' ? 'IOS' : 'Android']) }}
          </button>
          <div
            class="pointer-events-none absolute top-0 right-0 h-[60px] w-[32px] bg-gradient-to-l from-white to-[rgba(255,255,255,0)] rtl:right-auto rtl:left-0 rtl:bg-gradient-to-r"
            aria-hidden="true"
          ></div>
        </div>
        <div
          class="group relative mt-[20px] ml-[-30px] w-[1260px] rtl:mr-[-30px] rtl:ml-0"
          :class="brandAppCarouselHeightClass"
        >
          <div
            class="relative left-[30px] h-full w-[1200px] cursor-grab touch-pan-y overflow-hidden rounded-[8px] select-none active:cursor-grabbing rtl:right-[30px] rtl:left-auto"
            @pointerdown="startCarouselSwipe($event, 'brand')"
            @pointermove="moveCarouselPointer"
            @pointerup="endCarouselSwipe"
            @pointercancel="cancelCarouselSwipe"
            @dragstart.prevent
          >
            <div class="flex h-full gap-[20px] transition-transform duration-300 ease-out" :style="brandAppTrackStyle">
              <div
                v-for="(column, columnIndex) in brandAppColumns"
                :key="columnIndex"
                class="flex h-full w-[386.6667px] shrink-0 flex-col gap-[20px]"
              >
                <NuxtLink
                  v-for="app in column"
                  :key="`${app.icon}-${app.downloads}`"
                  :to="{ path: getBrandAppPath(app), query: getBrandAppQuery() }"
                  target="_blank"
                  class="relative flex h-[100px] w-full shrink-0 cursor-pointer items-start gap-[12px] rounded-[8px] border-[0.5px] border-[#0b122824] p-[14px] hover:no-underline"
                >
                  <div class="relative size-[44px] shrink-0">
                    <CommonImage
                      :src="app.icon"
                      :alt="app.name"
                      width="44"
                      height="44"
                      class="rounded-[4px] border border-solid border-[rgba(5,21,36,0.06)]"
                    />
                    <div
                      class="absolute top-[1px] right-[1px] z-[1] flex size-[14px] items-center justify-center rounded-tr-[4px] rounded-bl-[4px] bg-[rgba(5,12,31,0.46)] rtl:right-auto rtl:left-[1px] rtl:rounded-tl-[4px] rtl:rounded-tr-[0] rtl:rounded-br-[4px] rtl:rounded-bl-[0]"
                      aria-hidden="true"
                    >
                      <CommonImage :src="platformIconSrc" alt="" width="10" height="10" class="shrink-0" />
                    </div>
                  </div>
                  <div class="min-w-0 flex-1">
                    <h3 class="truncate text-[16px] leading-[22px] font-medium text-[#292a2e]">
                      <bdi>{{ app.name }}</bdi>
                    </h3>
                    <p class="mt-[4px] truncate text-[14px] leading-[20px] text-[#7d818a]">
                      <bdi>{{ app.desc }}</bdi>
                    </p>
                    <div class="mt-[4px] flex items-center gap-[4px] text-[14px] leading-[22px] text-[#7d818a]">
                      <CommonImage
                        src="https://resources1.interface003.com/web30/2026/09/08/c4a015b4-046f-41ac-b8ac-5177dec7895a.png"
                        alt=""
                        width="14"
                        height="14"
                        class="shrink-0"
                      />
                      <span dir="ltr">{{ app.downloads }}</span>
                    </div>
                  </div>
                  <div
                    v-if="app.online"
                    class="flex shrink-0 items-center gap-[6px] self-center text-[14px] leading-[20px] text-[#22a06b]"
                  >
                    <span class="size-[3px] rounded-full bg-[#22a06b]" aria-hidden="true"></span>
                    <span>{{ t('89352') }}</span>
                  </div>
                  <span v-else class="shrink-0 self-center text-[14px] leading-[20px] text-[#b7b9be]">
                    {{ t('89353') }}
                  </span>
                </NuxtLink>
              </div>
            </div>
            <div
              v-if="!visibleBrandApps.length"
              class="flex h-full items-center justify-center text-[14px] leading-[20px] text-[#7d818a]"
            >
              {{ t('83714') }}
            </div>
          </div>
          <template v-if="maxBrandAppIndex > 0">
            <button
              v-show="activeBrandAppIndex > 0"
              type="button"
              aria-label="上一个同品牌软件"
              class="pointer-events-none absolute top-1/2 left-[10px] z-[2] -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 rtl:right-[10px] rtl:left-auto"
              @click="moveBrandApps(-1)"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/07/0afaca60-bf8f-4d04-9230-08da7c33858f.png"
                alt=""
                width="20"
                height="34"
                class="rotate-180 rtl:rotate-0"
              />
            </button>
            <button
              v-show="activeBrandAppIndex < maxBrandAppIndex"
              type="button"
              aria-label="下一个同品牌软件"
              class="pointer-events-none absolute top-1/2 right-[10px] z-[2] -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 rtl:right-auto rtl:left-[10px]"
              @click="moveBrandApps(1)"
            >
              <CommonImage
                src="https://resources1.interface003.com/web30/2026/09/07/0afaca60-bf8f-4d04-9230-08da7c33858f.png"
                alt=""
                width="20"
                height="34"
                class="rtl:rotate-180"
              />
            </button>
          </template>
        </div>
      </section>
    </div>
  </main>
</template>

<style scoped lang="less">
.review-content-collapsed-flow {
  display: -webkit-box;
  overflow: hidden;
  line-height: 20px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
}

.review-content-no-ellipsis {
  display: block;
  max-height: 120px;
}

.preview-review-content-collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.preview-review-detail-content-collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
}

.software-details-hero {
  isolation: isolate;
  background-color: #4681fa;
  background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0.8) 100%), var(--software-hero-image);
  background-position: center;
  background-size: cover;

  .software-details-hero-rotate {
    position: absolute;
    z-index: 2;
    inset-block-start: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 500%;
    pointer-events: none;
    background-image: var(--software-hero-image);
    background-position: center;
    background-size: 100% auto;
    background-repeat: repeat;
    filter: brightness(1.3) saturate(0) blur(50px);
    mix-blend-mode: overlay;
    opacity: 0;
    transform-origin: center top;
    /* Match the App Store's idle-time motion rather than binding it to page scroll. */
    animation: software-hero-shift 60s linear 10s infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  .software-details-hero-blur {
    position: absolute;
    z-index: 1;
    inset: 0;
    pointer-events: none;
    backdrop-filter: blur(100px) saturate(1.5);
  }

  .software-details-hero-icon::before {
    content: '';
    position: absolute;
    z-index: -1;
    inset: 0;
    pointer-events: none;
    background: var(--software-hero-image) center / cover;
    filter: blur(60px);
    transform: scale(1.5);
  }
}

@keyframes software-hero-shift {
  0% {
    background-position: 50% 50%;
    background-size: 100% auto;
    opacity: 0;
    transform: rotate(0deg);
  }

  10% {
    opacity: 0.5;
  }

  20% {
    background-position: 65% 25%;
    background-size: 160% auto;
    transform: rotate(45deg);
  }

  45% {
    background-position: 90% 60%;
    background-size: 250% auto;
    opacity: 0.5;
    transform: rotate(160deg);
  }

  70% {
    background-position: 70% 40%;
    background-size: 200% auto;
    opacity: 0.5;
    transform: rotate(250deg);
  }

  100% {
    background-position: 50% 50%;
    background-size: 100% auto;
    opacity: 0;
    transform: rotate(360deg);
  }
}

.review-dialog-scrollbar {
  &::-webkit-scrollbar {
    display: block;
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 8px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
  }
}

.review-dialog-loading {
  position: relative;
  width: 64px;
  height: 64px;
}

.review-dialog-loading div {
  position: absolute;
  top: 27px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #f5bc49;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
  transform: translateZ(0);
  will-change: transform;
}

.review-dialog-loading div:nth-child(1) {
  left: 6px;
  animation: review-dialog-loading-1 0.6s infinite;
}

.review-dialog-loading div:nth-child(2),
.review-dialog-loading div:nth-child(3) {
  left: 6px;
  animation: review-dialog-loading-2 0.6s infinite;
}

.review-dialog-loading div:nth-child(3) {
  left: 26px;
}

.review-dialog-loading div:nth-child(4) {
  left: 45px;
  animation: review-dialog-loading-3 0.6s infinite;
}

@keyframes review-dialog-loading-1 {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes review-dialog-loading-2 {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(19px, 0);
  }
}

@keyframes review-dialog-loading-3 {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
}

.screenshot-preview-image {
  display: inline-block;
  width: max-content;
  height: 80vh;
  max-width: calc(100vw - 120px);
  overflow: visible;

  :deep(.main-image) {
    width: auto;
    height: 80vh;
    max-width: calc(100vw - 120px);
  }
}
</style>
